import { useEffect, useState, useCallback , useRef} from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { useAuth, API } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Eye, EyeOff, Rocket, Power, RefreshCw, Trash2, Save, Activity, TrendingUp, Shield, Zap, LogOut, Sparkles, Square, OctagonX, ListOrdered } from "lucide-react";
import { SetupWizard } from "@/pages/SetupWizard";

const SECTION = "rounded-lg border border-zinc-800 bg-zinc-950/60 p-6";
const HDR = "text-[11px] uppercase tracking-[0.2em] text-amber-500/90 font-mono mb-4 flex items-center gap-2";
const FIELD_LABEL = "text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono";

const STRATEGY_TEMPLATES = [
  {
    id: "s2_nifty_price_cross_66",
    label: "Strategy 2 · NIFTY ITM2 · Price Cross 66/86/116",
    description: "Premium cross 66→74, 86→99, 116→129 | SL 2pt | max 5 fires | CE+PE",
    patch: {
      name: "S2 NIFTY Price Cross 66",
      exchange: "NSE",
      exchange_segment: "NSE_FNO",
      symbol: "NIFTY",
      instrument_family: "Option",
      side: "BUY",
      option_type: "CE",
      moneyness: "ITM2",
      strike_selection: "AUTO",
      expiry_type: "CURRENT",
      quantity: 1,
      entry_type: "MARKET",
      entry_price: 0,
      sl_points: 2,
      target_gap: 8,
      t1: 74,
      t2: 0,
      t3: 0,
      t1_qty_pct: 100,
      t2_qty_pct: 0,
      t3_qty_pct: 0,
      trailing_sl: 0,
      trailing_mode: "NONE",
      target_trail_enabled: false,
      re_entry_on_sl: false,
      re_entry_on_target: false,
      max_reentries: 0,
      signal_enabled: true,
      signal_type: "PRICE_CROSS_UP",
      signal_watch: "BOTH",
      signal_cross_level: 66,
      signal_target_price: 74,
      signal_max_fires: 5,
      signal_fire_count: 0,
      signal_price_levels: [
        { level: 66, target: 74, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
        { level: 86, target: 99, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
        { level: 116, target: 129, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
      ],
    },
  },
  {
    id: "s2_banknifty_price_cross_66",
    label: "Strategy 2 · BANKNIFTY ITM2 · Price Cross 66/86/116",
    description: "BN premium cross 66→74, 86→99, 116→129 | SL 2pt | max 5 | CE+PE",
    patch: {
      name: "S2 BANKNIFTY Price Cross 66/86/116",
      exchange: "NSE",
      exchange_segment: "NSE_FNO",
      symbol: "BANKNIFTY",
      instrument_family: "Option",
      side: "BUY",
      option_type: "CE",
      moneyness: "ITM2",
      strike_selection: "AUTO",
      expiry_type: "CURRENT",
      quantity: 1,
      entry_type: "MARKET",
      entry_price: 0,
      sl_points: 2,
      target_gap: 8,
      t1: 74,
      t2: 0,
      t3: 0,
      t1_qty_pct: 100,
      t2_qty_pct: 0,
      t3_qty_pct: 0,
      trailing_sl: 0,
      trailing_mode: "NONE",
      target_trail_enabled: false,
      re_entry_on_sl: false,
      re_entry_on_target: false,
      max_reentries: 0,
      signal_enabled: true,
      signal_type: "PRICE_CROSS_UP",
      signal_watch: "BOTH",
      signal_cross_level: 66,
      signal_target_price: 74,
      signal_max_fires: 5,
      signal_fire_count: 0,
      signal_price_levels: [
        { level: 66, target: 74, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
        { level: 86, target: 99, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
        { level: 116, target: 129, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
      ],
    },
  },
  {
    id: "s2_sensex_price_cross_66",
    label: "Strategy 2 · SENSEX ITM2 · Price Cross 66/86/116",
    description: "Sensex premium cross 66→74, 86→99, 116→129 | SL 2pt | max 5 | CE+PE",
    patch: {
      name: "S2 SENSEX Price Cross 66/86/116",
      exchange: "BSE",
      exchange_segment: "BSE_FNO",
      symbol: "SENSEX",
      instrument_family: "Option",
      side: "BUY",
      option_type: "CE",
      moneyness: "ITM2",
      strike_selection: "AUTO",
      expiry_type: "CURRENT",
      quantity: 1,
      entry_type: "MARKET",
      entry_price: 0,
      sl_points: 2,
      target_gap: 8,
      t1: 74,
      t2: 0,
      t3: 0,
      t1_qty_pct: 100,
      t2_qty_pct: 0,
      t3_qty_pct: 0,
      trailing_sl: 0,
      trailing_mode: "NONE",
      target_trail_enabled: false,
      re_entry_on_sl: false,
      re_entry_on_target: false,
      max_reentries: 0,
      signal_enabled: true,
      signal_type: "PRICE_CROSS_UP",
      signal_watch: "BOTH",
      signal_cross_level: 66,
      signal_target_price: 74,
      signal_max_fires: 5,
      signal_fire_count: 0,
      signal_price_levels: [
        { level: 66, target: 74, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
        { level: 86, target: 99, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
        { level: 116, target: 129, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
      ],
    },
  },
  {
    id: "s1_nifty_itm2_bb_mid_1h",
    label: "Strategy 1 · NIFTY ITM2 · 1H BB Mid Cross",
    description: "",
    patch: {
      name: "S1 NIFTY ITM2 BB-Mid 1H",
      exchange: "NSE",
      exchange_segment: "NSE_FNO",
      symbol: "NIFTY",
      instrument_family: "Option",
      side: "BUY",
      option_type: "CE",
      moneyness: "ITM2",
      strike_selection: "AUTO",
      expiry_type: "CURRENT",
      quantity: 1,
      entry_type: "MARKET",
      entry_price: 0,
      sl_points: 10,
      target_gap: 10,
      t1: 0,
      t2: 0,
      t3: 0,
      t1_qty_pct: 100,
      t2_qty_pct: 0,
      t3_qty_pct: 0,
      trailing_sl: 5,
      trailing_mode: "COMMON",
      target_trail_enabled: true,
      target_trail_jump: 5,
      strategy_mtm_sl: 2000,
      strategy_mtm_target: 2000,
      re_entry_on_sl: true,
      re_entry_on_target: false,
      max_reentries: 2,
      reentry_grace_seconds: 5,
      product_type: "INTRADAY",
      qty_split_mode: "PERCENT",
      cond_vwap: false,
      cond_ema: false,
      cond_rsi: false,
      cond_bb: true,
      cond_sqrt_ema: false,
  cond_sqrt_ema_gap_pct: 5,
  cond_bb_cross: true,
  cond_bb_use_sma: false,
  cond_ema20: false,
  cond_ema50: false,
  cond_ema50_tf: 5,
  cond_ema200_tf: 5,
  cond_ema200: false,
  cond_fvg_bullish: false,
  cond_fvg_sl_special: false,
  cond_fvg_sl_buffer: 0,
      cond_bb_period: 15,
      cond_bb_stddev: 2,
      cond_sma_period: 15,
      cond_mode: "ANY",
      cond_on: "OPTION",
      signal_enabled: true,
      signal_type: "BB_MID_CROSS_1H",
      signal_watch: "BOTH",
      notes: "Auto: 1H close above BB middle on NIFTY ITM2 CE/PE → buy next candle.",
    },
  },
];



const DEFAULT_STRATEGY = {
  name: "Intraday Algo",
  exchange: "NSE",
  exchange_segment: "NSE_FNO",
  symbol: "BANKNIFTY",
  instrument_family: "Option",
  side: "BUY",
  option_type: "CE",
  moneyness: "ATM",
  strike_selection: "AUTO",
  manual_strike: 0,
  expiry_type: "CURRENT",
  lot_size: 0,
  quantity: 1,
  entry_type: "LIMIT",
  entry_price: 0,
  entry_band_points: 0,
  limit_price: 119,
  sl_points: 2,
  target_gap: 10,
  t1: 0,
  t2: 0,
  t3: 0,
  t1_qty_pct: 25,
  t2_qty_pct: 25,
  t3_qty_pct: 50,
  trailing_sl: 5,
  trailing_mode: "COMMON",
  trail_t1: 0,
  trail_t2: 0,
  trail_t3: 0,
  step_level: 0,
  target_trail_enabled: false,
  target_trail_jump: 0,
  target_trail_active: false,
  strategy_mtm_sl: 1000,
  strategy_mtm_target: 2000,
  re_entry_on_sl: true,
  re_entry_on_target: false,
  max_reentries: 5,
  reentry_count: 0,
  reentry_grace_seconds: 5,
  signal_enabled: false,
  signal_type: "NONE",
  signal_watch: "BOTH",
  signal_cross_level: 66,
  signal_target_price: 74,
  signal_max_fires: 5,
  signal_fire_count: 0,
  signal_price_levels: [
    { level: 66, target: 74, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
    { level: 86, target: 99, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
    { level: 116, target: 129, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
  ],
  entry_filter: "NONE",
  cond_vwap: false,
  cond_confirm_candle: false,
  cond_volume_filter: false,
  cond_volume_avg_period: 20,
  cond_vwap_min_dist: 0,
  cond_ema: false,
  cond_ema_period: 20,
  cond_ema_tf: 5,
  cond_rsi: false,
  cond_rsi_period: 14,
  cond_rsi_level: 50,
  cond_rsi_tf: 5,
  cond_bb: false,
  cond_bb_period: 15,
  cond_bb_stddev: 2,
  cond_sqrt_ema: false,
  cond_sma_period: 15,
  cond_no_lower_wick_30m: false,
  special_vol_sl_mode: "POINTS",
  special_vol_sl_buffer: 0,
  special_vol_target_pts: 10,
  special_vol_target_trail_pts: 0,
  no_lower_wick_tolerance: 0.05,
  no_lower_wick_tf: 30,
  trail_sl_from_next_open: false,
  candle_trail_points: 4,
  cond_mode: "ANY",
  cond_on: "OPTION",
  qty_split_mode: "EQUAL",
  entry_time: "09:15:00",
  exit_time: "23:15",
  product_type: "INTRADAY",
  status: "DRAFT",
  locked_security_id: "",
  locked_strike: 0,
  locked_lot_size: 0,
  locked_trading_symbol: "",
  locked_expiry: "",
};


// Partner / tools-mode UI: hide Entry Signals + Multi Wait Price from UI.
// Set HIDE_PARTNER_SECTIONS = false and rebuild frontend to show again when owner asks.
const HIDE_PARTNER_SECTIONS = true;

function StatusPill({ label, value, tone = "zinc" }) {
  const tones = {
    green: "bg-emerald-950 text-emerald-400 border-emerald-900",
    red: "bg-rose-950 text-rose-400 border-rose-900",
    amber: "bg-amber-950 text-amber-400 border-amber-900",
    zinc: "bg-zinc-900 text-zinc-300 border-zinc-800",
  };
  return (
    <span className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider rounded border ${tones[tone]}`} data-testid={`pill-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <span className="opacity-60 mr-1.5">{label}</span>
      <span>{value}</span>
    </span>
  );
}

function Field({ label, children, span = 1 }) {
  return (
    <div className={`flex flex-col gap-1.5 col-span-${span}`}>
      <Label className={FIELD_LABEL}>{label}</Label>
      {children}
    </div>
  );
}

export default function Console() {
  const { user, logout } = useAuth();
  const [showWizard, setShowWizard] = useState(false);
  const [broker, setBroker] = useState({
    broker: "dhan",
    dhan_client_id: "",
    access_token: "",
    totp_secret: "",
    broker_pin: "",
    consumer_key: "",
    consumer_secret: "",
    kotak_consumer_key: "",
    kotak_mobile: "",
    kotak_ucc: "",
    kotak_mpin: "",
    kotak_access_token: "",
    kotak_sid: "",
    kotak_totp_secret: "",
    mode: "PAPER",
    auto_square_off: "15:15",
    max_loss: -5000,
    max_profit: 10000,
    kill_switch: false,
    telegram_bot_token: "",
    telegram_chat_id: "",
    telegram_enabled: false,
    proxy_enabled: false,
    proxy_type: "http",
    proxy_host: "",
    proxy_port: 0,
    proxy_username: "",
    proxy_password: "",
  });
  const [publicCfg, setPublicCfg] = useState({ app_name: "Dhan Algo Console", static_ip: "", secondary_static_ip: "", auto_square_off: "15:15", max_orders_per_second: 10 });
  const [marketPulse, setMarketPulse] = useState(null);
  const [scannerPresets, setScannerPresets] = useState([]);
  const [scannerId, setScannerId] = useState("stock_screanner_001");
  const [scannerResult, setScannerResult] = useState(null);
  const [scannerLoading, setScannerLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    const loadPulse = async () => {
      try {
        const r = await axios.get(`${API}/market/pulse`);
        if (alive && r.data) setMarketPulse(r.data);
      } catch (e) {
        /* ignore pulse errors */
      }
    };
    loadPulse();
    const t = setInterval(loadPulse, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);


  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/scanner/presets`);
        const list = r.data?.presets || [];
        setScannerPresets(list);
        if (list.length) {
          setScannerId((cur) => (list.some((p) => p.id === cur) ? cur : list[0].id));
        }
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const [show, setShow] = useState({ token: false, secret: false, pin: false, totp: false });
  const [strategy, setStrategy] = useState(DEFAULT_STRATEGY);
  const [multiWaitText, setMultiWaitText] = useState("");
  const multiWaitMode = (multiWaitText || "").trim().length > 0;
  const [resolution, setResolution] = useState(null);
  const [sqrtEmaInfo, setSqrtEmaInfo] = useState(null);
  // Auto-fetch Sqrt EMA when strike/security is resolved
  useEffect(() => {
    const sid = strategy.locked_security_id || resolution?.security_id;
    const seg = resolution?.segment || resolution?.fno_segment || "NSE_FNO";
    if (!sid || sid === "—") {
      setSqrtEmaInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/indicators/sqrt-ema`, {
          params: { security_id: String(sid), segment: seg, is_option: true },
        });
        if (!cancelled) setSqrtEmaInfo(r.data || null);
      } catch (e) {
        if (!cancelled) setSqrtEmaInfo(null);
      }
    })();
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/indicators/sqrt-ema`, {
          params: { security_id: String(sid), segment: seg, is_option: true },
        });
        if (!cancelled) setSqrtEmaInfo(r.data || null);
      } catch (_) {}
    }, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [strategy.locked_security_id, resolution?.security_id, resolution?.segment, resolution?.fno_segment]);


  const [validation, setValidation] = useState(null);
  const [logs, setLogs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [trades, setTrades] = useState([]);
  const [tradesNetPnl, setTradesNetPnl] = useState(0);
  const [positions, setPositions] = useState([]);
  const [myStrategies, setMyStrategies] = useState([]);
  const refreshStrategies = useCallback(async () => {
    try {
      const st = await axios.get(`${API}/strategy`);
      setMyStrategies(Array.isArray(st.data) ? st.data : []);
    } catch (_) {
      /* keep previous list */
    }
  }, []);

  useEffect(() => {
    refreshStrategies();
    const t = setInterval(() => { refreshStrategies(); }, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshStrategies]);

  // ---- Trade alerts: toast + sound on execute / reject (English) ----
  const seenTradeLogIds = useRef(new Set());
  const tradeAlertsReady = useRef(false);

  const playTradeSound = useCallback((kind) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      const now = ctx.currentTime;
      if (kind === "ok") {
        o.type = "sine";
        o.frequency.setValueAtTime(880, now);
        o.frequency.setValueAtTime(1174, now + 0.08);
        g.gain.setValueAtTime(0.0001, now);
        g.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
        o.start(now);
        o.stop(now + 0.3);
      } else {
        o.type = "square";
        o.frequency.setValueAtTime(220, now);
        o.frequency.setValueAtTime(160, now + 0.12);
        g.gain.setValueAtTime(0.0001, now);
        g.gain.exponentialRampToValueAtTime(0.14, now + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
        o.start(now);
        o.stop(now + 0.38);
      }
      setTimeout(() => { try { ctx.close(); } catch (_) {} }, 500);
    } catch (_) {}
  }, []);

  const notifyTradeEvent = useCallback((entry) => {
    const kind = String(entry.kind || "").toUpperCase();
    const msg = String(entry.message || "");
    const m = msg.toUpperCase();
    let type = null; // ok | err
    let title = "";
    let body = msg;

    if (kind === "ORDER" || /^LIVE\s+(T[123]\s+)?(BUY|SELL)\b/i.test(msg) || /^PAPER\s+/i.test(msg)) {
      type = "ok";
      title = "Order placed";
    }
    if (/\bTRADED\b/i.test(msg) && /WS order update/i.test(msg)) {
      type = "ok";
      title = "Order executed";
      body = msg.replace(/^WS order update:\s*/i, "");
    }
    if (/\bTRIGGERED\b/i.test(msg) && /Super Order/i.test(msg)) {
      type = "ok";
      title = "SL / Target triggered";
      body = msg.replace(/^WS order update:\s*/i, "");
    }
    if (
      kind === "ERROR" ||
      /\bREJECT/i.test(msg) ||
      /\bDH-\d+/i.test(msg) ||
      /Invalid Price/i.test(msg) ||
      /Instant SL block/i.test(msg) ||
      /place failed/i.test(msg) ||
      /Momentum filter blocked/i.test(msg)
    ) {
      // Don't alert pure token validation noise as trade reject unless order-related
      if (/Token validation/i.test(msg) || /Auto-token/i.test(msg) || /Credentials/i.test(msg)) {
        return;
      }
      type = "err";
      title = "Order rejected / blocked";
    }
    if (!type) return;

    const toastId = `trade-${entry.id || entry.ts || body.slice(0, 40)}`;
    if (type === "ok") {
      toast.success(`${title}: ${body}`, { id: toastId, duration: 8000 });
      playTradeSound("ok");
    } else {
      toast.error(`${title}: ${body}`, { id: toastId, duration: 12000 });
      playTradeSound("err");
    }
  }, [playTradeSound]);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/logs?limit=40`);
        const rows = Array.isArray(r.data) ? r.data : [];
        if (!cancelled) setLogs(rows);
        // Newest first typically — process oldest of batch first for ordering
        const batch = [...rows].reverse();
        for (const entry of batch) {
          const id = entry.id || `${entry.ts}|${entry.kind}|${entry.message}`;
          if (seenTradeLogIds.current.has(id)) continue;
          seenTradeLogIds.current.add(id);
          // Skip flood of history on first load
          if (!tradeAlertsReady.current) continue;
          notifyTradeEvent(entry);
        }
        if (!tradeAlertsReady.current) {
          tradeAlertsReady.current = true;
        }
        // Cap set size
        if (seenTradeLogIds.current.size > 500) {
          const arr = [...seenTradeLogIds.current];
          seenTradeLogIds.current = new Set(arr.slice(-300));
        }
      } catch (_) {}
    };
    poll();
    const t = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(t); };
  }, [notifyTradeEvent]);

  const [loadingExitId, setLoadingExitId] = useState(null);
  const [funds, setFunds] = useState(null);
  const [livePrices, setLivePrices] = useState({ indices: {}, strike: null });
  const [hideFunds, setHideFunds] = useState(() => {
    try { return localStorage.getItem("rachi_hide_funds") === "1"; } catch { return false; }
  });
  const [desktopView, setDesktopView] = useState(() => {
    try {
      const saved = localStorage.getItem("rachi_desktop_view");
      if (saved === "1") return true;
      if (saved === "0") return false;
      // first visit: wide screen → desktop layout
      if (typeof window !== "undefined" && window.innerWidth >= 1024) return true;
      return false;
    } catch { return typeof window !== "undefined" && window.innerWidth >= 1024; }
  });
  const resolutionRef = useRef(null);
  const strategyRefLive = useRef(null);

  useEffect(() => {
    let alive = true;
    const SEG_FALLBACK = {
      NIFTY: "NSE_FNO", BANKNIFTY: "NSE_FNO", FINNIFTY: "NSE_FNO", MIDCPNIFTY: "NSE_FNO",
      SENSEX: "BSE_FNO", BANKEX: "BSE_FNO",
      CRUDEOIL: "MCX_COMM", CRUDEOILM: "MCX_COMM", NATURALGAS: "MCX_COMM", NATGASMINI: "MCX_COMM", NATURALGASM: "MCX_COMM", GOLD: "MCX_COMM", SILVER: "MCX_COMM",
    };
    const tick = async () => {
      try {
        const res = resolutionRef.current;
        const st = strategyRefLive.current || {};
        const params = {};
        // Prefer Derived Details (Scrip Master resolution) — NOT a random LIVE strategy
        if (res?.security_id) {
          params.security_id = String(res.security_id);
          const sym = (st.symbol || res.symbol || "").toUpperCase();
          const VALID = new Set(["NSE_FNO","BSE_FNO","NSE_EQ","BSE_EQ","MCX_COMM","IDX_I"]);
          const cand = res.segment || res.exchange_segment || "";
          params.segment = VALID.has(String(cand)) ? String(cand) : (SEG_FALLBACK[sym] || "NSE_FNO");
          if (res.strike != null) params.strike = res.strike;
          if (res.trading_symbol) params.trading_symbol = res.trading_symbol;
          if (sym) params.symbol = sym;
          if (st.option_type || res.option_type) params.option_type = st.option_type || res.option_type;
          if (st.entry_price) params.entry_price = st.entry_price;
        }
        const r = await axios.get(`${API}/market/live-prices`, { params });
        if (alive) setLivePrices(r.data || { indices: {}, strike: null });
      } catch (e) {
        console.error("live-prices", e);
      }
    };
    tick();
    const t = setInterval(tick, 12000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  useEffect(() => { resolutionRef.current = resolution; }, [resolution]);
  useEffect(() => { strategyRefLive.current = strategy; }, [strategy]);
  useEffect(() => {
    try { localStorage.setItem("rachi_hide_funds", hideFunds ? "1" : "0"); } catch {}
  }, [hideFunds]);
  useEffect(() => {
    const narrowPhone = typeof window !== "undefined" && window.innerWidth < 640;
    // Save preference (phones can still save false)
    try { localStorage.setItem("rachi_desktop_view", desktopView ? "1" : "0"); } catch {}
    const meta = document.querySelector('meta[name="viewport"]');
    const root = document.documentElement;
    const body = document.body;
    if (desktopView && !narrowPhone) {
      root.classList.add("rk-desktop-mode");
      root.classList.remove("rk-mobile-mode");
      root.style.minWidth = "1200px";
      body.style.minWidth = "1200px";
      root.style.overflowX = "auto";
      body.style.overflowX = "auto";
      if (meta) meta.setAttribute("content", "width=1200, initial-scale=1");
    } else {
      root.classList.add("rk-mobile-mode");
      root.classList.remove("rk-desktop-mode");
      root.style.minWidth = "";
      body.style.minWidth = "";
      root.style.overflowX = "hidden";
      body.style.overflowX = "hidden";
      if (meta) meta.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover");
    }
    return () => {
      root.classList.remove("rk-desktop-mode", "rk-mobile-mode");
      root.style.minWidth = "";
      body.style.minWidth = "";
      body.style.overflowX = "";
      if (meta) meta.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover");
    };
  }, [desktopView]);


  const [runtimeIp, setRuntimeIp] = useState(null);
  const [symbols, setSymbols] = useState({});
  const [loading, setLoading] = useState({ resolve: false, deploy: false, validate: false });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState(null);

  const brokerDirtyRef = useRef(false);
  const brokerAutosaveTimer = useRef(null);
  const brokerRef = useRef(broker);
  brokerRef.current = broker;

  const SENSITIVE_BROKER_FIELDS = ["access_token", "broker_pin", "totp_secret", "telegram_bot_token", "proxy_password"];

  const mergeBrokerFromServer = (prev, incoming) => {
    const data = { ...(incoming || {}) };
    // Never wipe secrets the user is typing / already has locally
    for (const f of SENSITIVE_BROKER_FIELDS) {
      const incomingVal = (data[f] ?? "").toString().trim();
      const prevVal = (prev[f] ?? "").toString().trim();
      if (!incomingVal && prevVal) {
        data[f] = prev[f];
      }
    }
    // While user is editing credentials, keep local sensitive + client id fields
    if (brokerDirtyRef.current) {
      for (const f of [...SENSITIVE_BROKER_FIELDS, "dhan_client_id", "telegram_chat_id", "proxy_host", "proxy_username", "proxy_password"]) {
        if ((prev[f] ?? "").toString().length > 0) data[f] = prev[f];
      }
      // Keep toggles / port user just flipped until save settles
      data.proxy_enabled = prev.proxy_enabled;
      data.telegram_enabled = prev.telegram_enabled;
      if (prev.proxy_port) data.proxy_port = prev.proxy_port;
    }
    return { ...prev, ...data };
  };

  const refresh = useCallback(async () => {
    try {
      const [b, s, l, o, p, pc, ip, fnd, tr] = await Promise.all([
        axios.get(`${API}/broker/config`),
        axios.get(`${API}/scrip/symbols`),
        axios.get(`${API}/logs?limit=150`),
        axios.get(`${API}/orders`).catch(() => ({ data: { orders: [] } })),
        axios.get(`${API}/positions`).catch(() => ({ data: { positions: [] } })),
        axios.get(`${API}/config/public`).catch(() => ({ data: {} })),
        axios.get(`${API}/broker/runtime-ip`).catch(() => ({ data: {} })),
        axios.get(`${API}/funds`).catch(() => ({ data: { funds: null } })),
        axios.get(`${API}/trades`).catch(() => ({ data: { trades: [], net_pnl: 0 } })),
      ]);
      setBroker((prev) => mergeBrokerFromServer(prev, b.data));
      setSymbols(s.data.symbols || {});
      setLogs(l.data || []);
      setOrders(o.data.orders || []);
      setPositions(p.data.positions || []);
      setTrades(tr.data?.trades || []);
      setTradesNetPnl(Number(tr.data?.net_pnl || 0));
      try {
        const st = await axios.get(`${API}/strategy`);
        setMyStrategies(Array.isArray(st.data) ? st.data : []);
      } catch (_) {
        setMyStrategies([]);
      }
      setRuntimeIp(ip.data || null);
      setFunds(fnd.data?.funds || null);
      if (pc.data?.app_name) setPublicCfg(pc.data);
      document.title = pc.data?.app_name || "Dhan Algo Console";
    } catch (e) {
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleBrokerAutosave = useCallback(() => {
    brokerDirtyRef.current = true;
    if (brokerAutosaveTimer.current) clearTimeout(brokerAutosaveTimer.current);
    brokerAutosaveTimer.current = setTimeout(async () => {
      const payload = brokerRef.current;
      try {
        await axios.post(`${API}/broker/config`, payload);
        brokerDirtyRef.current = false;
        toast.success("Credentials auto-saved", { id: "broker-autosave", duration: 1500 });
      } catch (e) {
        toast.error("Auto-save failed: " + (e.response?.data?.detail || e.message));
      }
    }, 1200);
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh]);

  // Auto-open wizard for first-time users (no broker config + no strategies + flag not set)
  useEffect(() => {
    const dismissed = localStorage.getItem("rachi_wizard_dismissed");
    if (dismissed) return;
    if (broker.dhan_client_id) return; // already configured
    // small delay to let initial refresh settle
    const t = setTimeout(() => setShowWizard(true), 500);
    return () => clearTimeout(t);
  }, [broker.dhan_client_id]);

  const applyCreds = async () => {
    setLoading((x) => ({ ...x, validate: true }));
    try {
      if (brokerAutosaveTimer.current) clearTimeout(brokerAutosaveTimer.current);
      await axios.post(`${API}/broker/config`, broker);
      brokerDirtyRef.current = false;
      const v = await axios.post(`${API}/broker/validate`);
      setValidation(v.data);
      if (v.data.valid) {
        const bal = v.data.available_balance != null ? Number(v.data.available_balance) : null;
        const nm = v.data.dhan_client_name || v.data.profile?.dhanClientName || "";
        toast.success(
          bal != null
            ? `Connected · ${nm || "OK"} · Balance ₹${bal.toFixed(2)} · Credentials LOCKED`
            : `Credentials validated. Engine ready. Credentials LOCKED`
        );
      } else {
        toast.error(v.data.message || "Token validation failed. Check credentials.");
      }
      refresh();
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading((x) => ({ ...x, validate: false }));
    }
  };

  const autoToken = async () => {
    setLoading((x) => ({ ...x, validate: true }));
    try {
      const r = await axios.post(`${API}/broker/auto-token`);
      const tok = r.data?.access_token;
      if (tok) {
        setBroker((b) => ({ ...b, access_token: tok }));
        brokerDirtyRef.current = false;
      }
      const exp = r.data?.expiryTime || r.data?.token_validity || "—";
      if (r.data?.valid) {
        setValidation({
          valid: true,
          message: r.data.message,
          available_balance: r.data.available_balance,
          dhan_client_name: r.data.dhanClientName,
          credentials_locked: true,
          data_plan: r.data.data_plan,
          data_validity: r.data.data_validity,
          token_validity: r.data.token_validity || r.data.expiryTime,
        });
        setBroker((b) => ({
          ...b,
          access_token: tok || b.access_token,
          credentials_locked: true,
          token_expiry: r.data.expiryTime || "",
          token_validity: r.data.token_validity || r.data.expiryTime || "",
          data_plan: r.data.data_plan || "",
          data_validity: r.data.data_validity || "",
        }));
        const bal = r.data.available_balance != null ? Number(r.data.available_balance) : null;
        toast.success(
          bal != null
            ? `Auto-token OK · expires ${exp} · Balance ₹${bal.toFixed(2)} · LOCKED`
            : `Auto-token OK · expires ${exp} · LOCKED`
        );
      } else {
        toast.success(`Token saved · expires ${exp}`);
        // still try validate (backend already tried inline)
        try {
          const v = await axios.post(`${API}/broker/validate`);
          setValidation(v.data);
          if (v.data.valid) toast.success("Validate OK · Credentials LOCKED");
          else toast.error(v.data.message || "Token still invalid — check Client ID + paste from Dhan API section");
        } catch (ve) {
          toast.error("Validate failed: " + (ve.response?.data?.detail || ve.message));
        }
      }
      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Auto-token failed";
      const status = e.response?.status;
      if (status === 401 || String(detail).toLowerCase().includes("token expired") || String(detail).toLowerCase().includes("not authenticated")) {
        toast.error("Login session expired — logout/login, then Auto-Generate. (Dhan se copy-paste tabhi jab PIN+TOTP save na ho)");
        try { logout && logout(); } catch (_) {}
      } else if (String(detail).toLowerCase().includes("pin") || String(detail).toLowerCase().includes("totp") || String(detail).toLowerCase().includes("client id")) {
        toast.error(detail);
      } else {
        toast.error("Auto-token failed: " + detail);
      }
    } finally {
      setLoading((x) => ({ ...x, validate: false }));
    }
  };



  const unlockCreds = async () => {
    setLoading((x) => ({ ...x, validate: true }));
    try {
      await axios.post(`${API}/broker/unlock-credentials`, {});
      toast.success("Credentials unlocked — you can edit now. Re-validate to lock again.");
      setBroker((b) => ({ ...b, credentials_locked: false }));
      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Unlock failed";
      const status = e.response?.status;
      if (status === 401 || String(detail).toLowerCase().includes("token expired") || String(detail).toLowerCase().includes("session expired") || String(detail).toLowerCase().includes("not authenticated")) {
        toast.error("Session expired — login again, then Unlock / paste new Dhan token.");
        try { logout && logout(); } catch (_) {}
      } else {
        toast.error("Unlock failed: " + detail);
      }
    } finally {
      setLoading((x) => ({ ...x, validate: false }));
    }
  };

  const resolveScrip = async () => {
    setLoading((x) => ({ ...x, resolve: true }));
    try {
      const r = await axios.get(`${API}/scrip/resolve`, {
        params: {
          symbol: strategy.symbol,
          instrument_family: strategy.instrument_family,
          option_type: strategy.option_type,
          moneyness: strategy.moneyness,
          expiry_type: strategy.expiry_type,
          manual_strike: strategy.manual_strike,
          strike_selection: strategy.strike_selection,
        },
      });
      {
        const sym = (strategy.symbol || "").toUpperCase();
        const SEG = { NIFTY: "NSE_FNO", BANKNIFTY: "NSE_FNO", FINNIFTY: "NSE_FNO", MIDCPNIFTY: "NSE_FNO", SENSEX: "BSE_FNO", BANKEX: "BSE_FNO", CRUDEOIL: "MCX_COMM", CRUDEOILM: "MCX_COMM", NATURALGAS: "MCX_COMM", NATGASMINI: "MCX_COMM", NATURALGASM: "MCX_COMM" };
        {
          const VALID = new Set(["NSE_FNO","BSE_FNO","NSE_EQ","BSE_EQ","MCX_COMM","IDX_I"]);
          const cand = r.data.segment || r.data.exchange_segment || symbols?.[strategy.symbol]?.fno_segment || "";
          const seg = VALID.has(String(cand)) ? String(cand) : (SEG[sym] || "NSE_FNO");
          setResolution({
            ...r.data,
            symbol: r.data.symbol || strategy.symbol,
            option_type: r.data.option_type || strategy.option_type,
            segment: seg,
          });
        }
      }
      if (r.data.lot_size || r.data.security_id) {
        setStrategy((s) => ({
          ...s,
          ...(r.data.lot_size ? { lot_size: r.data.lot_size } : {}),
          ...(r.data.security_id ? {
            locked_security_id: String(r.data.security_id),
            locked_strike: Number(r.data.strike || s.locked_strike || 0),
            locked_lot_size: Number(r.data.lot_size || s.lot_size || 0),
            locked_trading_symbol: r.data.trading_symbol || s.locked_trading_symbol || "",
            locked_expiry: r.data.expiry || s.locked_expiry || "",
          } : {}),
        }));
      }
      if (!r.data.security_id) {
        toast.warning("Resolve incomplete — no security_id. Check token / MANUAL strike.");
      } else {
        const src = r.data.spot_source ? ` · spot via ${r.data.spot_source}` : "";
        const optPx = r.data.option_ltp != null ? ` · opt ₹${Number(r.data.option_ltp).toFixed(2)}` : "";
        const spotPx = r.data.spot_ltp != null ? ` · spot ₹${Number(r.data.spot_ltp).toFixed(2)}` : "";
        const warn = (!r.data.spot_ltp && !r.data.option_ltp) ? " (spot/opt LTP offline — verify strike)" : "";
        const sub = r.data.strike_substituted ? " · snapped to listed strike" : "";
        toast.success(`Resolved: ${r.data.trading_symbol || "contract"} · strike ${r.data.strike}${spotPx}${optPx}${src}${sub}${warn}`);
      }
    } catch (e) {
      toast.error("Resolve failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading((x) => ({ ...x, resolve: false }));
    }
  };

  // Auto-fill T1/T2/T3 targets from entry_price + target_gap
  const autoTarget = (n) => {
    const ep = Number(strategy.entry_price || 0);
    const gap = Number(strategy.target_gap || 0);
    if (!ep || !gap) return 0;
    const delta = n * gap;
    return strategy.side === "SELL"
      ? Number((ep - delta).toFixed(2))
      : Number((ep + delta).toFixed(2));
  };
  const t1Effective = strategy.t1 || autoTarget(1);
  const t2Effective = strategy.t2 || autoTarget(2);
  const t3Effective = strategy.t3 || autoTarget(3);

  const resetReentry = async () => {
    if (!strategy.id || strategy.id.startsWith("draft-")) {
      toast.error("Save the strategy first before resetting re-entries.");
      return;
    }
    try {
      await axios.post(`${API}/strategy/${strategy.id}/reset-reentry`);
      setStrategy((s) => ({ ...s, reentry_count: 0 }));
      toast.success("Re-entry counter reset to 0");
    } catch (e) {
      toast.error("Reset failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const toggleMode = async (toLive) => {
    const newMode = toLive ? "LIVE" : "PAPER";
    // optimistic UI
    setBroker((b) => ({ ...b, mode: newMode }));
    try {
      // mode-only endpoint (avoids full config validation issues)
      await axios.post(`${API}/broker/mode`, { mode: newMode });
      toast.success(`Mode switched to ${newMode}${newMode === "LIVE" ? " — real orders WILL be placed" : " — simulation only"}`);
    } catch (e) {
      try {
        // fallback: merge into existing broker config
        await axios.post(`${API}/broker/config`, { ...(broker || {}), mode: newMode, broker: "dhan" });
        toast.success(`Mode switched to ${newMode}`);
      } catch (e2) {
        setBroker((b) => ({ ...b, mode: toLive ? "PAPER" : "LIVE" }));
        toast.error("Failed to switch mode: " + (e2.response?.data?.detail || e.response?.data?.detail || e.message));
      }
    }
  };

  const buildConfirmSummary = (resOverride) => {
    const res = resOverride || resolution || {};
    const locked = !!(strategy.locked_security_id);
    const isLimit = (strategy.entry_type || "LIMIT").toUpperCase() !== "MARKET";
    const liveOpt = Number(livePrices?.strike?.ltp || 0);
    // LIMIT: only form entry_price (never stale limit_price). MARKET: 0 until live LTP (preview only).
    let buyPrice = 0;
    let buyLabel = "";
    if (isLimit) {
      buyPrice = Number(strategy.entry_price) > 0 ? Number(strategy.entry_price) : 0;
      buyLabel = buyPrice > 0 ? String(buyPrice) : "— (set Entry Price or use Multi Wait)";
    } else {
      // MARKET: do not show leftover limit/entry as buy price
      if (liveOpt > 0) {
        buyPrice = liveOpt;
        buyLabel = `MARKET ≈ LTP ${liveOpt}`;
      } else {
        buyPrice = 0;
        buyLabel = "MARKET @ open/live LTP (not fixed)";
      }
    }
    const side = strategy.side || "BUY";
    const slPts = Number(strategy.sl_points) || 0;
    const gap = Number(strategy.target_gap) || 0;
    // t1/t2/t3 may be stored as POINTS or as absolute target prices (autoTarget fills absolute).
    // If value is already on the profit side of buyPrice, treat as absolute — do NOT add again.
    const _tgtLevel = (explicit, mult) => {
      const v = Number(explicit) || 0;
      const ptsFallback = gap > 0 ? mult * gap : 0;
      if (buyPrice > 0) {
        if (v > 0) {
          if (side === "BUY" && v > buyPrice) return Math.round(v * 100) / 100; // absolute
          if (side === "SELL" && v > 0 && v < buyPrice) return Math.round(v * 100) / 100; // absolute
          // points
          return Math.round((buyPrice + (side === "BUY" ? v : -v)) * 100) / 100;
        }
        if (ptsFallback > 0) {
          return Math.round((buyPrice + (side === "BUY" ? ptsFallback : -ptsFallback)) * 100) / 100;
        }
        return 0;
      }
      // no entry yet — show points for preview
      return v > 0 ? v : ptsFallback;
    };
    const t1pts = Number(strategy.t1) > 0 ? Number(strategy.t1) : gap;
    const t2pts = Number(strategy.t2) > 0 ? Number(strategy.t2) : (gap > 0 ? 2 * gap : 0);
    const t3pts = Number(strategy.t3) > 0 ? Number(strategy.t3) : (gap > 0 ? 3 * gap : 0);
    const sl = buyPrice > 0
      ? (side === "BUY" ? Math.round((buyPrice - slPts) * 100) / 100 : Math.round((buyPrice + slPts) * 100) / 100)
      : 0;
    const t1 = _tgtLevel(strategy.t1, 1);
    const t2 = _tgtLevel(strategy.t2, 2);
    const t3 = _tgtLevel(strategy.t3, 3);
    const qty = Math.max(1, Number(strategy.quantity) || 1);
    let t1Lots, t2Lots, t3Lots;
    if (strategy.qty_split_mode === "EQUAL") {
      t1Lots = Math.floor(qty / 3);
      t2Lots = Math.floor(qty / 3);
      t3Lots = qty - t1Lots - t2Lots;
    } else {
      t1Lots = Math.round((Number(strategy.t1_qty_pct) || 0) / 100 * qty);
      t2Lots = Math.round((Number(strategy.t2_qty_pct) || 0) / 100 * qty);
      t3Lots = Math.round((Number(strategy.t3_qty_pct) || 0) / 100 * qty);
    }
    const trailMode = strategy.trailing_mode || "COMMON";
    const trailPts = trailMode === "INDIVIDUAL"
      ? `T1=${strategy.trail_t1 || 0} T2=${strategy.trail_t2 || 0} T3=${strategy.trail_t3 || 0}`
      : String(strategy.trailing_sl || 0);
    return {
      side,
      mode: broker.mode,
      symbol: strategy.symbol,
      option_type: strategy.option_type,
      strike: locked ? strategy.locked_strike : (res.strike || strategy.manual_strike || "—"),
      trading_symbol: locked ? (strategy.locked_trading_symbol || "—") : (res.trading_symbol || "—"),
      security_id: locked ? strategy.locked_security_id : (res.security_id || "—"),
      expiry: locked ? (strategy.locked_expiry || "—") : (res.expiry || "—"),
      locked,
      entry_type: isLimit ? "LIMIT" : "MARKET",
      targetTrail: strategy.target_trail_enabled ? `ON · ${strategy.target_trail_jump || 0} pts` : "OFF",
      buyPrice: buyLabel || buyPrice,
      buyPriceNum: buyPrice,
      sl: buyPrice > 0 ? sl : `SL ${slPts} pts`,
      t1: buyPrice > 0 ? t1 : (t1pts ? `${t1pts} pts` : 0),
      t2: buyPrice > 0 ? t2 : (t2pts ? `${t2pts} pts` : 0),
      t3: buyPrice > 0 ? t3 : (t3pts ? `${t3pts} pts` : 0),
      slPts, t1pts, t2pts, t3pts,
      t1Lots, t2Lots, t3Lots,
      totalLots: qty,
      trailMode,
      trailPts,
      reEntrySL: !!strategy.re_entry_on_sl,
      reEntryTarget: !!strategy.re_entry_on_target,
      maxReentries: strategy.max_reentries,
      product: strategy.product_type,
    };
  };


  const exitStrategy = async (sid, label) => {
    if (!sid) return;
    const ok = window.confirm(
      `STOP / EXIT this strategy?\n\n${label || sid}\n\n` +
      `• Open algo orders for THIS strategy will be cancelled on Dhan\n` +
      `• Pending re-entry will be cleared\n` +
      `• Other strategies / other symbols will NOT be touched`
    );
    if (!ok) return;
    setLoadingExitId(sid);
    try {
      const r = await axios.post(`${API}/strategy/${sid}/exit`);
      toast.success(`Stopped · cancelled ${(r.data.cancelled || []).length} order(s)`);
      if (strategy.id === sid) {
        setStrategy((s) => ({ ...s, status: "STOPPED", pending_reentry: false, price_wait_enabled: false }));
      }
      await refresh();
    } catch (e) {
      toast.error("Exit failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingExitId(null);
    }
  };

  const loadStrategyIntoForm = (s) => {
    setStrategy({ ...DEFAULT_STRATEGY, ...s });
    if (s.locked_security_id) {
      setResolution({
        security_id: s.locked_security_id,
        strike: s.locked_strike,
        lot_size: s.locked_lot_size,
        trading_symbol: s.locked_trading_symbol,
        expiry: s.locked_expiry,
        symbol: s.symbol,
        option_type: s.option_type,
      });
    }
    toast.message(`Loaded: ${s.name || s.symbol} · ${s.status}`);
  };


  const runStockScanner = async (force = false) => {
    setScannerLoading(true);
    try {
      const r = await axios.post(`${API}/scanner/run`, { preset_id: scannerId, force });
      setScannerResult(r.data);
      if (r.data?.ok) {
        toast.success(`${r.data.name}: ${r.data.match_count || 0} matches (${r.data.scanned || 0} scanned)`);
      } else {
        toast.error(r.data?.error || "Scanner failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Scanner failed");
    } finally {
      setScannerLoading(false);
    }
  };

  const openDeployConfirm = async () => {
    if (broker.kill_switch) {
      toast.error("Kill switch is ON. Release it first before deploying.");
      return;
    }
    let resNow = resolution;
    if (!strategy.locked_security_id && !resNow?.security_id) {
      try {
        const r = await axios.get(`${API}/scrip/resolve`, {
          params: {
            symbol: strategy.symbol,
            instrument_family: strategy.instrument_family,
            option_type: strategy.option_type,
            moneyness: strategy.moneyness,
            expiry_type: strategy.expiry_type,
            manual_strike: strategy.manual_strike,
            strike_selection: strategy.strike_selection,
          },
        });
        resNow = r.data;
        setResolution(r.data);
        if (r.data.lot_size) setStrategy((s) => ({ ...s, lot_size: r.data.lot_size }));
      } catch (e) {
        toast.error("Resolve failed: " + (e.response?.data?.detail || e.message));
        return;
      }
    }
    const summary = buildConfirmSummary(resNow);
    if (!summary.security_id || summary.security_id === "—") {
      toast.error("Contract not resolved. Check strike / token.");
      return;
    }
    const isMkt = (summary.entry_type || strategy.entry_type || "").toUpperCase() === "MARKET";
    if (!isMkt && (!summary.buyPrice || summary.buyPrice <= 0)) {
      toast.error("LIMIT order: Set entry price (cannot be 0)");
      return;
    }
    if (isMkt && (!summary.buyPrice || summary.buyPrice <= 0)) {
      // still allow deploy — backend uses live LTP for SL/target
      toast.message?.("MARKET: entry from live LTP") || toast.success("MARKET deploy — SL/Target from live LTP");
    }
    if (summary.buyPrice > 0 && summary.side === "BUY") {
      if (summary.sl >= summary.buyPrice) {
        toast.error(`BUY SL (₹${summary.sl}) must be below entry (₹${summary.buyPrice})`);
        return;
      }
      if (summary.t1Lots > 0 && summary.t1 <= summary.buyPrice) {
        toast.error(`BUY T1 (₹${summary.t1}) must be above entry`);
        return;
      }
    } else if (summary.buyPrice > 0 && summary.side === "SELL") {
      if (summary.sl <= summary.buyPrice) {
        toast.error(`SELL SL (₹${summary.sl}) must be above entry (₹${summary.buyPrice})`);
        return;
      }
    }
    setConfirmSummary(summary);
    setConfirmOpen(true);
  };

  const deploy = async () => {
    setConfirmOpen(false);
    setLoading((x) => ({ ...x, deploy: true }));
    try {
      const saved = await axios.post(`${API}/strategy`, strategy);
      const d = await axios.post(`${API}/strategy/${saved.data.id}/deploy`);
      const reso = d.data?.resolution || {};
      setStrategy({
        ...saved.data,
        status: d.data?.status || saved.data.status,
        locked_security_id: reso.security_id || saved.data.locked_security_id || "",
        locked_strike: reso.strike ?? saved.data.locked_strike ?? 0,
        locked_lot_size: reso.lot_size || saved.data.locked_lot_size || 0,
        locked_trading_symbol: reso.trading_symbol || saved.data.locked_trading_symbol || "",
        locked_expiry: reso.expiry || reso.expiry_date || saved.data.locked_expiry || "",
      });
      if (reso.security_id) setResolution((prev) => ({ ...(prev || {}), ...reso }));
      toast.success(`${broker.mode} deploy: ${d.data.status} · ${(d.data.orders || []).length} legs`);
      refresh();
    } catch (e) {
      toast.error("Deploy failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading((x) => ({ ...x, deploy: false }));
    }
  };

  
  const testProxy = async () => {
    setLoading((x) => ({ ...x, validate: true }));
    try {
      // save current proxy fields first
      await axios.post(`/broker/config`, brokerRef.current);
      const r = await axios.post(`/broker/proxy-test`);
      if (r.data?.ok) {
        toast.success(r.data.message || `Exit IP: ${r.data.exit_ip}`);
      } else {
        toast.error(r.data?.message || "Proxy test failed");
      }
      if (r.data?.dhan_profile_ok === false) {
        toast.error(r.data?.dhan_message || "Dhan profile failed via proxy");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Proxy test error");
    } finally {
      setLoading((x) => ({ ...x, validate: false }));
    }
  };

const testTelegram = async () => {
    try {
      await axios.post(`${API}/broker/config`, broker);
      const r = await axios.post(`${API}/telegram/test`);
      if (r.data.ok) toast.success("Telegram alert delivered");
      else toast.error("Telegram failed: " + JSON.stringify(r.data.data).slice(0, 80));
    } catch (e) {
      toast.error("Telegram test failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const killSwitch = async () => {
    try {
      const r = await axios.post(`${API}/broker/kill-switch?enable=${!broker.kill_switch}`);
      setBroker((b) => ({ ...b, kill_switch: r.data.kill_switch }));
      toast[r.data.kill_switch ? "error" : "success"](
        r.data.kill_switch ? "KILL SWITCH engaged" : "Kill switch released"
      );
      refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  
  const clearContractLock = (s) => ({
    ...s,
    locked_security_id: "",
    locked_strike: 0,
    locked_lot_size: 0,
    locked_trading_symbol: "",
    locked_expiry: "",
  });

  const applyStrategyTemplate = (templateId) => {
    const t = STRATEGY_TEMPLATES.find((x) => x.id === templateId);
    if (!t) return;
    setStrategy((s) => clearContractLock({
      ...s,
      ...t.patch,
      status: "DRAFT",
    }));
    setResolution(null);
    toast.success("Applied: " + t.label);
  };

  const clearLogs = async () => {
    await axios.delete(`${API}/logs`);
    toast.success("Logs cleared");
    refresh();
  };

  const tokenOk = validation?.valid === true;
  const tokenSet = !!broker.access_token || !!broker.token_set;
  const modeTone = broker.mode === "LIVE" ? "red" : "amber";
  const tokenLabel = tokenOk ? "VALID" : tokenSet ? (validation?.valid === false ? "INVALID" : "SET") : "NONE";
  const tokenTone = tokenOk ? "green" : tokenSet ? "amber" : "red";

  return (
    <div className="min-h-screen bg-black text-zinc-100 font-sans overflow-x-hidden" data-testid="app-root">
      
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md w-[calc(100%-1.5rem)] sm:w-full mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-400 font-mono tracking-wide">
              Final Order Confirmation
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-zinc-300 space-y-3 font-mono pt-2">
                {confirmSummary && (
                  <>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[12px]">
                      <span className="text-zinc-500">Mode</span>
                      <span className={confirmSummary.mode === "LIVE" ? "text-rose-400 font-bold" : "text-amber-400"}>{confirmSummary.mode}</span>
                      <span className="text-zinc-500">Symbol</span>
                      <span>{confirmSummary.symbol} {confirmSummary.strike}{confirmSummary.option_type}</span>
                      <span className="text-zinc-500">Contract</span>
                      <span className="truncate">{confirmSummary.trading_symbol}</span>
                      <span className="text-zinc-500">Security ID</span>
                      <span className="text-amber-400">{confirmSummary.security_id}{confirmSummary.locked ? " · LOCKED" : ""}</span>
                      <span className="text-zinc-500">Expiry</span>
                      <span>{confirmSummary.expiry}</span>
                      <span className="text-zinc-500">Side / Product</span>
                      <span>{confirmSummary.side} · {confirmSummary.product}</span>
                      <span className="text-zinc-500">Order Type</span>
                      <span>{confirmSummary.entry_type}</span>
                      <span className="text-zinc-500">Buy / Entry Price</span>
                      <span className="text-emerald-400 font-bold">{typeof confirmSummary.buyPrice === "number" ? `₹${confirmSummary.buyPrice}` : confirmSummary.buyPrice}</span>
                      <span className="text-zinc-500">Stop Loss</span>
                      <span className="text-rose-400 font-bold">{typeof confirmSummary.sl === "number" ? `₹${confirmSummary.sl}` : confirmSummary.sl}</span>
                      <span className="text-zinc-500">T1 / T2 / T3</span>
                      <span>{[confirmSummary.t1, confirmSummary.t2, confirmSummary.t3].map((x) => typeof x === "number" ? `₹${x}` : x).join(" · ")}</span>
                      <span className="text-zinc-500">Lots (T1/T2/T3)</span>
                      <span>{confirmSummary.t1Lots} / {confirmSummary.t2Lots} / {confirmSummary.t3Lots} (total {confirmSummary.totalLots})</span>
                      <span className="text-zinc-500">Trailing</span>
                      <span>{confirmSummary.trailMode} · {confirmSummary.trailPts} pts</span>
                      <span className="text-zinc-500">Re-Entry SL</span>
                      <span className={confirmSummary.reEntrySL ? "text-amber-400" : "text-zinc-500"}>{confirmSummary.reEntrySL ? "ON" : "OFF"}</span>
                      <span className="text-zinc-500">Re-Entry Target</span>
                      <span className={confirmSummary.reEntryTarget ? "text-amber-400" : "text-zinc-500"}>{confirmSummary.reEntryTarget ? "ON" : "OFF"}</span>
                      <span className="text-zinc-500">Max Re-entries</span>
                      <span>{confirmSummary.maxReentries}</span>
                      <span className="text-zinc-500">Target Trail</span>
                      <span>{confirmSummary.targetTrail}</span>
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel className="bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); deploy(); }}
              className={confirmSummary?.mode === "LIVE" ? "bg-rose-600 hover:bg-rose-500 text-white" : "bg-amber-500 hover:bg-amber-400 text-black"}
            >
              Accept & Deploy
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Toaster theme="dark" position="top-right" richColors />
      {showWizard && (
        <SetupWizard
          onComplete={() => { setShowWizard(false); localStorage.setItem("rachi_wizard_dismissed", "1"); refresh(); }}
          onSkip={() => { setShowWizard(false); localStorage.setItem("rachi_wizard_dismissed", "1"); }}
        />
      )}

      {/* Top bar */}
      <div className="sticky top-0 z-30 border-b border-zinc-900 bg-black/90 backdrop-blur">
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 shrink-0 rounded bg-gradient-to-br from-amber-400 to-orange-600 grid place-items-center font-black text-black">D</div>
            <div className="min-w-0">
              <div className="text-base sm:text-lg font-bold tracking-tight truncate">{publicCfg.app_name}</div>
              <div className="hidden sm:block text-[10px] text-zinc-500 font-mono uppercase tracking-widest">NSE · BSE · MCX · Super Orders</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
            <StatusPill label="Mode" value={broker.mode} tone={modeTone} />
            <StatusPill label="Token" value={tokenLabel} tone={tokenTone} />
            <StatusPill label="Kill" value={broker.kill_switch ? "ON" : "OFF"} tone={broker.kill_switch ? "red" : "green"} />
            <Button variant="ghost" size="sm" onClick={() => setHideFunds((v) => !v)} data-testid="hide-funds-btn" className="text-zinc-400" title={hideFunds ? "Show balance" : "Hide balance"}>{hideFunds ? "Show ₹" : "Hide ₹"}</Button>
            <Button variant="ghost" size="sm" onClick={() => setDesktopView((v) => !v)} data-testid="desktop-view-btn" className={"text-zinc-400 " + (desktopView ? "text-amber-400" : "")} title="Toggle wide desktop vs responsive layout">{desktopView ? "Desktop ✓" : "Mobile layout"}</Button>
            <Button variant="ghost" size="sm" onClick={refresh} data-testid="refresh-btn" className="text-zinc-400"><RefreshCw className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => setShowWizard(true)} data-testid="open-wizard-btn" className="text-amber-400" title="Setup Wizard"><Sparkles className="h-4 w-4" /></Button>
            <div className="hidden sm:flex items-center gap-1 ml-2 pl-3 border-l border-zinc-800">
              <span className="text-xs font-mono text-zinc-400 truncate max-w-[140px]" data-testid="user-email">{user?.email}</span>
              <Button onClick={logout} variant="ghost" size="sm" className="text-zinc-400" data-testid="logout-btn"><LogOut className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
        {/* Market pulse — NSE-style compact strip */}
        {marketPulse?.ok && (
          <div className="px-4 sm:px-6 pb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] sm:text-[11px] font-mono text-zinc-400 border-t border-zinc-900/80 pt-2" data-testid="market-pulse">
            <span className="text-zinc-500 uppercase tracking-wider">NIFTY50</span>
            <span title="Stocks traded (NIFTY 50 universe)">Traded <b className="text-zinc-200">{marketPulse.breadth?.traded ?? "—"}</b></span>
            <span className="text-emerald-400">Adv <b>{marketPulse.breadth?.advances ?? "—"}</b></span>
            <span className="text-rose-400">Dec <b>{marketPulse.breadth?.declines ?? "—"}</b></span>
            <span>Unch <b className="text-zinc-300">{marketPulse.breadth?.unchanged ?? "—"}</b></span>
            <span className="text-zinc-700">|</span>
            <span className="text-amber-300">VIX <b>{marketPulse.india_vix?.india_vix ?? "—"}</b>
              {marketPulse.india_vix?.chg != null && (
                <span className={Number(marketPulse.india_vix.chg) >= 0 ? " text-rose-400" : " text-emerald-400"}>
                  {" "}({Number(marketPulse.india_vix.chg) >= 0 ? "+" : ""}{marketPulse.india_vix.chg})
                </span>
              )}
            </span>
            <span className="text-zinc-700">|</span>
            <span>PCR Nifty <b className="text-sky-300">{marketPulse.pcr?.NIFTY ?? "—"}</b></span>
            <span>BN <b className="text-sky-300">{marketPulse.pcr?.BANKNIFTY ?? "—"}</b></span>
            <span>Sensex <b className="text-sky-300">{marketPulse.pcr?.SENSEX ?? "—"}</b></span>
          </div>
        )}
      </div>

      <div className="rk-main-grid grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 p-3 sm:p-4 lg:p-6 pb-36 sm:pb-32">
        {/* Static IP info banner — visible in both modes */}
        {runtimeIp && !runtimeIp.matches && runtimeIp.whitelisted_primary && (
          <div className={`lg:col-span-12 rounded-lg px-4 py-3 text-[11px] sm:text-xs font-mono flex items-start gap-3 break-words ${broker.mode === "LIVE" ? "bg-rose-950/60 border border-rose-900 text-rose-300" : "bg-amber-950/40 border border-amber-900/60 text-amber-300"}`} data-testid="ip-warning">
            <Shield className={`h-4 w-4 ${broker.mode === "LIVE" ? "text-rose-400" : "text-amber-400"}`} />
            <span>
              <strong className={broker.mode === "LIVE" ? "text-rose-200" : "text-amber-200"}>STATIC IP {broker.mode === "LIVE" ? "MISMATCH" : "NOTICE"}</strong>
              {" · "}Pod IP <code className="text-zinc-200">{runtimeIp.runtime_ip}</code> ≠ Whitelisted <code className="text-zinc-200">{runtimeIp.whitelisted_primary}</code>.
              {broker.mode === "LIVE" ? " Live orders WILL be rejected (DH-905). " : " Switching to LIVE will fail until IPs match. "}
              Either run from your whitelisted machine or update at web.dhan.co → Static IP.
            </span>
          </div>
        )}
        {/* Live index + NIFTY ITM2 CE/PE */}
        <div className="lg:col-span-12 rounded-lg border border-sky-900/50 bg-sky-950/30 px-4 py-3 text-[11px] sm:text-xs text-zinc-300 space-y-1.5" data-testid="dhan-data-api-notice">
          <div className="font-semibold text-sky-300">Full algo features require Dhan Data API subscription</div>
          <p className="text-zinc-400 leading-relaxed">
            Live LTP, ATM resolve, charts, and indicator auto-entry use Dhan Data APIs. Subscribe / manage from your Dhan profile.{" "}
            <a href="https://web.dhan.co/index/profile" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300 underline underline-offset-2">
              Open Dhan Profile →
            </a>
          </p>
          <p className="text-zinc-500">
            Paper mode works on live market data (live-like, no real orders). No account yet? ₹0 opening · ₹0 AMC · may get 2 free premium TradingView indicators.{" "}
            <a href="https://invite.dhan.co/?join=SUS165" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">
              Join Dhan →
            </a>
          </p>
        </div>
        <div className="lg:col-span-12 grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 mb-1 w-full" data-testid="live-prices-bar">
          {["NIFTY", "BANKNIFTY", "SENSEX"].map((k) => {
            const row = livePrices?.indices?.[k];
            const ltp = row?.ltp;
            const chg = row?.change;
            const up = chg != null && Number(chg) >= 0;
            const priceCls = ltp == null ? "text-zinc-500" : ((chg == null || up) ? "text-emerald-400" : "text-rose-400");
            const chgCls = chg == null ? "text-zinc-600" : (up ? "text-emerald-500" : "text-rose-500");
            const chgTxt = chg == null ? "—" : ((up ? "+" : "") + Number(chg).toFixed(2) + " pts");
            return (
              <div key={k} className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2">
                <div className="text-[9px] text-zinc-500 uppercase tracking-wider">{k}</div>
                <div className={"text-base font-mono " + priceCls}>
                  {ltp != null ? ("\u20B9" + Number(ltp).toFixed(2)) : "—"}
                </div>
                <div className={"text-[10px] font-mono " + chgCls}>{chgTxt}</div>
              </div>
            );
          })}
          {(() => {
            const st = livePrices?.strike;
            const hasStrike = st && (st.security_id || st.trading_symbol || st.label);
            const secId = st?.security_id != null ? String(st.security_id) : null;
            const pos = (positions || []).find((x) => {
              if (!secId) return false;
              return String(x.securityId || x.security_id || '') === secId;
            });
            const ltp = st?.ltp != null ? Number(st.ltp) : (pos?.ltp != null ? Number(pos.ltp) : null);
            const entry = st?.entry_price != null ? Number(st.entry_price) : (pos?.buyAvg != null ? Number(pos.buyAvg) : (pos?.costPrice != null ? Number(pos.costPrice) : null));
            const pnlRaw = pos ? Number(pos.unrealizedProfit ?? 0) + Number(pos.realizedProfit ?? 0) : (entry != null && ltp != null ? (ltp - entry) : null);
            const chg = (entry != null && ltp != null) ? (ltp - entry) : null;
            const up = chg == null ? null : chg >= 0;
            const title = hasStrike
              ? (st.trading_symbol || st.label || [st.symbol, st.strike, st.option_type].filter(Boolean).join(' '))
              : (livePrices?.nifty_itm2?.atm ? ('NIFTY ITM2 · ATM ' + livePrices.nifty_itm2.atm) : 'Selected / Last order');
            const priceCls = ltp == null ? 'text-zinc-500' : (chg == null || up ? 'text-amber-400' : 'text-rose-400');
            const chgCls = chg == null ? 'text-zinc-600' : (up ? 'text-emerald-500' : 'text-rose-500');
            const pnlCls = pnlRaw == null ? 'text-zinc-600' : (pnlRaw >= 0 ? 'text-emerald-400' : 'text-rose-400');
            if (!hasStrike && livePrices?.nifty_itm2) {
              return (
                <div className="bg-zinc-950 border border-amber-900/50 rounded px-3 py-2" data-testid="live-selected-scrip">
                  <div className="text-[9px] text-amber-500/80 uppercase tracking-wider">{title}</div>
                  <div className="grid grid-cols-2 gap-1 mt-0.5">
                    <div>
                      <div className="text-[9px] text-zinc-500">CE {livePrices?.nifty_itm2?.ce_strike ?? '—'}</div>
                      <div className="text-amber-400 text-sm font-mono">{livePrices?.nifty_itm2?.ce?.ltp != null ? ('₹' + Number(livePrices.nifty_itm2.ce.ltp).toFixed(2)) : '—'}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-zinc-500">PE {livePrices?.nifty_itm2?.pe_strike ?? '—'}</div>
                      <div className="text-amber-400 text-sm font-mono">{livePrices?.nifty_itm2?.pe?.ltp != null ? ('₹' + Number(livePrices.nifty_itm2.pe.ltp).toFixed(2)) : '—'}</div>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div className="bg-zinc-950 border border-amber-900/50 rounded px-3 py-2" data-testid="live-selected-scrip">
                <div className="text-[9px] text-amber-500/80 uppercase tracking-wider truncate" title={title}>{title || 'Scrip'}</div>
                <div className={"text-base font-mono " + priceCls}>{ltp != null ? ('₹' + ltp.toFixed(2)) : '—'}</div>
                <div className="flex justify-between gap-2 text-[10px] font-mono mt-0.5">
                  <span className={chgCls}>{chg == null ? '—' : ((up ? '+' : '') + chg.toFixed(2))}</span>
                  <span className={pnlCls}>{pnlRaw == null ? 'P&L —' : ('P&L ' + (pnlRaw >= 0 ? '+' : '') + pnlRaw.toFixed(2))}</span>
                </div>
              </div>
            );
          })()}
        </div>
        {funds && !hideFunds && (
          <div className="lg:col-span-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 text-xs font-mono w-full">
            <div className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2"><div className="text-[9px] text-zinc-500 uppercase">Available</div><div className="text-emerald-400 text-base" data-testid="funds-available">₹{funds.availabelBalance?.toFixed(2) ?? "—"}</div></div>
            <div className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2"><div className="text-[9px] text-zinc-500 uppercase">SOD Limit</div><div className="text-zinc-200 text-base">₹{funds.sodLimit?.toFixed(2) ?? "—"}</div></div>
            <div className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2"><div className="text-[9px] text-zinc-500 uppercase">Utilized</div><div className="text-amber-400 text-base">₹{funds.utilizedAmount?.toFixed(2) ?? "—"}</div></div>
            <div className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2"><div className="text-[9px] text-zinc-500 uppercase">Withdrawable</div><div className="text-zinc-200 text-base">₹{funds.withdrawableBalance?.toFixed(2) ?? "—"}</div></div>
            <div className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2"><div className="text-[9px] text-zinc-500 uppercase">{(broker.broker || "dhan") === "kotak" ? "UCC" : "Client"}</div><div className="text-zinc-300 text-base truncate">{funds.dhanClientId || broker.kotak_ucc || "—"}</div></div>
          </div>
        )}
        {/* Sidebar */}
        <aside className="lg:col-span-3 space-y-4 order-3 lg:order-1 w-full min-w-0">
          <Card className="bg-zinc-950/80 border-zinc-800 p-5">
            <div className={HDR}><Shield className="h-3 w-3" /> Broker Credentials {broker.credentials_locked ? <span className="ml-2 text-emerald-400 text-[10px]">LOCKED</span> : <span className="ml-2 text-amber-400 text-[10px]">EDITABLE</span>}</div>
            {broker.credentials_locked && (
              <div className="text-[11px] text-emerald-300/90 font-mono mb-2 px-2 py-1.5 rounded border border-emerald-900 bg-emerald-950/40">
                Connected & locked{broker.last_validate_name ? ` · ${broker.last_validate_name}` : ""}
                <div className="text-zinc-500 mt-1">{(broker.broker || "dhan") === "kotak" ? "Kotak credentials locked. Daily session: Login / Daily Session (1 click). Unlock only to edit keys." : "Click Unlock to edit credentials. Re-validate to lock again."}</div>
              </div>
            )}
            <div className="space-y-3">
              <Field label="Active Broker">
                <select
                  data-testid="select-active-broker"
                  value={broker.broker || broker.active_broker || "dhan"}
                  onChange={async (e) => {
                    const v = e.target.value;
                    const next = {
                      ...broker,
                      broker: v,
                      active_broker: v,
                      // allow editing the broker we switch to
                      credentials_locked: v === "kotak" ? !!broker.credentials_locked && v === "kotak" : false,
                    };
                    if (v === "kotak" && (broker.kotak_ucc || broker.kotak_mobile || broker.kotak_consumer_key_set)) {
                      next.credentials_locked = true;
                    }
                    if (v === "dhan") {
                      next.credentials_locked = false;
                    }
                    setBroker(next);
                    try {
                      const r = await axios.post(`${API}/broker/active`, {
                        broker: v,
                        active_broker: v,
                        unlock: v === "dhan",
                      });
                      toast.success(r.data?.message || `Switched to ${v}`);
                      // refresh config so Dhan/Kotak fields match server
                      try {
                        const cfg = await axios.get(`${API}/broker/config`);
                        if (cfg.data) {
                          setBroker((b) => ({
                            ...b,
                            ...cfg.data,
                            broker: v,
                            active_broker: v,
                            credentials_locked: v === "dhan" ? false : !!cfg.data.credentials_locked,
                            access_token: "",
                            broker_pin: "",
                            totp_secret: "",
                            kotak_mpin: "",
                            kotak_totp_secret: "",
                          }));
                        }
                      } catch (_) {}
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || err.message || "Switch failed");
                    }
                  }}
                  className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm font-mono text-zinc-200"
                >
                  <option value="dhan">Dhan</option>
                  <option value="kotak">Kotak Neo</option>
                </select>
              </Field>
              {(broker.broker || "dhan") === "dhan" && (
              <>
              <Field label="Dhan Client ID">
                <Input data-testid="input-client-id" disabled={!!broker.credentials_locked} value={broker.dhan_client_id} onChange={(e) => { setBroker({ ...broker, dhan_client_id: e.target.value }); scheduleBrokerAutosave(); }} className="bg-zinc-900 border-zinc-800 font-mono" />
              </Field>
              <Field label="Access Token (JWT)">
                <div className="relative">
                  <Input data-testid="input-access-token" disabled={!!broker.credentials_locked} type={show.token ? "text" : "password"} value={broker.access_token} placeholder={broker.token_set || broker.credentials_locked ? "•••• token saved on server (unchanged)" : "Paste Dhan access token"} onChange={(e) => { setBroker({ ...broker, access_token: e.target.value }); scheduleBrokerAutosave(); }} className="bg-zinc-900 border-zinc-800 font-mono pr-9" />
                  <button onClick={() => setShow({ ...show, token: !show.token })} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500">{show.token ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                </div>
              </Field>
              <Field label="Broker PIN (6-digit)">
                <Input data-testid="input-pin" disabled={!!broker.credentials_locked} type={show.pin ? "text" : "password"} value={broker.broker_pin} onChange={(e) => { setBroker({ ...broker, broker_pin: e.target.value }); scheduleBrokerAutosave(); }} className="bg-zinc-900 border-zinc-800 font-mono" />
              </Field>
              <Field label="TOTP Secret (optional)">
                <Input data-testid="input-totp" disabled={!!broker.credentials_locked} type={show.totp ? "text" : "password"} value={broker.totp_secret} onChange={(e) => { setBroker({ ...broker, totp_secret: e.target.value }); scheduleBrokerAutosave(); }} className="bg-zinc-900 border-zinc-800 font-mono" />
              </Field>
              </>
              )}
              {(broker.broker || "dhan") === "kotak" && (
              <>
              <div className="text-[10px] text-sky-400/90 font-mono px-1">Credentials auto-lock on Login. Daily change = one-click Login / Daily Session (TOTP auto). Balance shows after login.</div>
              <Field label="Consumer Key">
                <Input data-testid="input-kotak-key" disabled={!!broker.credentials_locked} value={broker.kotak_consumer_key || ""} onChange={(e) => { setBroker({ ...broker, kotak_consumer_key: e.target.value, consumer_key: e.target.value }); scheduleBrokerAutosave(); }} className="bg-zinc-900 border-zinc-800 font-mono" placeholder="Kotak API consumer key" />
              </Field>
              <Field label="Mobile">
                <Input data-testid="input-kotak-mobile" disabled={!!broker.credentials_locked} value={broker.kotak_mobile || ""} onChange={(e) => { setBroker({ ...broker, kotak_mobile: e.target.value }); scheduleBrokerAutosave(); }} className="bg-zinc-900 border-zinc-800 font-mono" placeholder="Registered mobile" />
              </Field>
              <Field label="UCC">
                <Input data-testid="input-kotak-ucc" disabled={!!broker.credentials_locked} value={broker.kotak_ucc || ""} onChange={(e) => { setBroker({ ...broker, kotak_ucc: e.target.value }); scheduleBrokerAutosave(); }} className="bg-zinc-900 border-zinc-800 font-mono" placeholder="Trading UCC" />
              </Field>
              <Field label="MPIN">
                <Input data-testid="input-kotak-mpin" disabled={!!broker.credentials_locked} type="password" value={broker.kotak_mpin || ""} onChange={(e) => { setBroker({ ...broker, kotak_mpin: e.target.value }); scheduleBrokerAutosave(); }} className="bg-zinc-900 border-zinc-800 font-mono" placeholder={broker.kotak_mpin_set ? "•••• saved" : "MPIN"} />
              </Field>
              <Field label="Access Token (optional)">
                <Input data-testid="input-kotak-token" disabled={!!broker.credentials_locked} type="password" value={broker.kotak_access_token || ""} onChange={(e) => { setBroker({ ...broker, kotak_access_token: e.target.value }); scheduleBrokerAutosave(); }} className="bg-zinc-900 border-zinc-800 font-mono" placeholder={broker.kotak_access_token_set ? "•••• saved" : "Optional"} />
              </Field>
              <Field label="SID (optional)">
                <Input data-testid="input-kotak-sid" disabled={!!broker.credentials_locked} value={broker.kotak_sid || ""} onChange={(e) => { setBroker({ ...broker, kotak_sid: e.target.value }); scheduleBrokerAutosave(); }} className="bg-zinc-900 border-zinc-800 font-mono" />
              </Field>
              <Field label="TOTP Secret (authenticator)">
                <Input data-testid="input-kotak-totp-secret" disabled={!!broker.credentials_locked} type="password" value={broker.kotak_totp_secret || ""} onChange={(e) => { setBroker({ ...broker, kotak_totp_secret: e.target.value }); scheduleBrokerAutosave(); }} className="bg-zinc-900 border-zinc-800 font-mono" placeholder={broker.kotak_totp_secret_set ? "•••• saved" : "Base32 secret from Neo TOTP QR"} />
              </Field>
              <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" className="w-full border-emerald-700 text-emerald-300" data-testid="btn-kotak-login"
                onClick={async () => {
                  try {
                    // Save only non-empty Kotak fields so locked secrets are not wiped
                    const payload = { broker: "kotak", active_broker: "kotak" };
                    if (broker.kotak_consumer_key) payload.kotak_consumer_key = broker.kotak_consumer_key;
                    if (broker.kotak_mobile) payload.kotak_mobile = broker.kotak_mobile;
                    if (broker.kotak_ucc) payload.kotak_ucc = broker.kotak_ucc;
                    if (broker.kotak_mpin) payload.kotak_mpin = broker.kotak_mpin;
                    if (broker.kotak_totp_secret) payload.kotak_totp_secret = broker.kotak_totp_secret;
                    if (broker.kotak_sid) payload.kotak_sid = broker.kotak_sid;
                    try { await axios.post(`${API}/broker/config`, payload); } catch (_) {}
                    const r = await axios.post(`${API}/broker/kotak/login`, {});
                    const avail = r.data?.available_balance;
                    const funds = r.data?.funds;
                    if (funds) setFunds({
                      availabelBalance: funds.availabelBalance ?? funds.availableBalance,
                      availableBalance: funds.availableBalance ?? funds.availabelBalance,
                      utilizedAmount: funds.utilizedAmount,
                      sodLimit: funds.sodLimit,
                      withdrawableBalance: funds.withdrawableBalance,
                      dhanClientId: funds.brokerClientId || broker.kotak_ucc || "Kotak",
                    });
                    setBroker((b) => ({
                      ...b,
                      broker: "kotak",
                      credentials_locked: true,
                      last_funds_avail: avail ?? b.last_funds_avail,
                      last_validate_name: `Kotak ${b.kotak_ucc || ""}`.trim(),
                    }));
                    toast.success(
                      avail != null
                        ? `Kotak login OK · ₹${Number(avail).toFixed(2)}`
                        : (r.data?.message || "Kotak login OK")
                    );
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || e.message || "Kotak login failed");
                  }
                }}
              >Login / Daily Session</Button>
              <Button type="button" variant="outline" className="w-full border-zinc-600 text-zinc-300" data-testid="btn-kotak-logout"
                onClick={async () => {
                  try {
                    const r = await axios.post(`${API}/broker/kotak/logout`);
                    toast.success(r.data?.message || "Kotak session cleared — switch Active Broker to Dhan if needed");
                    // Do not remove Dhan from dropdown; only clear session state
                    setBroker((b) => ({ ...b, broker: b.broker || "kotak" }));
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || e.message || "Logout failed");
                  }
                }}
              >Logout Kotak Session</Button>
              </div>
              <Button type="button" variant="outline" className="w-full border-sky-800 text-sky-300" data-testid="btn-kotak-test"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/broker/config`, broker);
                    const r = await axios.post(`${API}/broker/kotak/test`);
                    if (r.data?.configured) toast.success(r.data.message || "Kotak OK");
                    else toast.error(r.data?.last_error || r.data?.message || "Kotak incomplete");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || e.message || "Kotak test failed");
                  }
                }}
              >Test Kotak Credentials</Button>
              </>
              )}

{(broker.broker || "dhan") === "dhan" && (<>
              <Button onClick={applyCreds} disabled={loading.validate || !!broker.credentials_locked} className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid="apply-credentials-btn">
                {loading.validate ? "Validating…" : (broker.credentials_locked ? "Locked — Unlock to edit" : "Apply Credentials & Validate")}
              </Button>
              <Button onClick={autoToken} disabled={loading.validate} variant="outline" className="w-full border-emerald-700 text-emerald-400 hover:bg-emerald-950" data-testid="auto-token-btn">
                <Zap className="h-4 w-4 mr-2" /> Auto-Generate Token (TOTP)
              </Button>
              </>)}
              {broker.credentials_locked ? (
                <Button onClick={unlockCreds} disabled={loading.validate} variant="outline" className="w-full border-zinc-600 text-zinc-200" data-testid="unlock-credentials-btn">
                  Unlock Credentials
                </Button>
              ) : null}
              {validation && (
                <div className={`text-[11px] font-mono px-3 py-2 rounded border ${validation.valid ? "border-emerald-900 bg-emerald-950/50 text-emerald-300" : "border-rose-900 bg-rose-950/50 text-rose-300"}`}>
                  {validation.valid || broker.last_validated_at
                  ? `OK · ${validation.dhan_client_name || validation.profile?.dhanClientName || broker.last_validate_name || "Validated"}${validation.available_balance != null ? ` · ₹${Number(validation.available_balance).toFixed(2)}` : (broker.last_funds_avail ? ` · ₹${Number(broker.last_funds_avail).toFixed(2)}` : "")}${(validation.credentials_locked || broker.credentials_locked) ? " · LOCKED" : ""}${(validation.data_plan || broker.data_plan) ? ` · DataPlan ${validation.data_plan || broker.data_plan}` : ""}${(validation.data_validity || broker.data_validity) ? ` · Data till ${validation.data_validity || broker.data_validity}` : ""}${(validation.token_validity || broker.token_validity || broker.token_expiry) ? ` · Token till ${validation.token_validity || broker.token_validity || broker.token_expiry}` : ""}`
                  : (validation.message || "Token validation failed")}
                </div>
              )}
            </div>
          </Card>

          <Card className="bg-zinc-950/80 border-zinc-800 p-5">
            <div className={HDR}><Zap className="h-3 w-3" /> Execution Mode</div>
            <div className="flex items-center justify-between bg-zinc-900 rounded px-3 py-2 mb-3">
              <div className="text-sm font-mono">{broker.mode === "LIVE" ? "🔴 Live" : "📝 Paper"}</div>
              <Switch data-testid="mode-toggle" checked={broker.mode === "LIVE"} onCheckedChange={toggleMode} />
            </div>
            <Field label="Auto Square-off"><Input value={broker.auto_square_off} onChange={(e) => setBroker({ ...broker, auto_square_off: e.target.value })} className="bg-zinc-900 border-zinc-800 font-mono" /></Field>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Field label="Max Loss (₹)"><Input type="number" value={broker.max_loss} onChange={(e) => setBroker({ ...broker, max_loss: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono" /></Field>
              <Field label="Max Profit (₹)"><Input type="number" value={broker.max_profit} onChange={(e) => setBroker({ ...broker, max_profit: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono" /></Field>
            </div>
            <Button onClick={killSwitch} data-testid="kill-switch-btn" className={`w-full mt-4 font-bold ${broker.kill_switch ? "bg-emerald-600 hover:bg-emerald-500" : "bg-rose-600 hover:bg-rose-500"}`}>
              <Power className="h-4 w-4 mr-2" /> {broker.kill_switch ? "Release Kill Switch" : "Trigger Kill Switch"}
            </Button>
          </Card>

          
          {/* Per-user Proxy / Own IP */}
          <Card className="bg-zinc-950/80 border-zinc-800 p-5">
            <div className={HDR}><Shield className="h-3 w-3" /> My Proxy / Own IP</div>
            <div className="space-y-3">
              <div className="text-[10px] text-zinc-500 font-mono leading-relaxed">
                Dhan allows one IP per account. If proxy needs login (HTTP 407), Username + Password are required — not optional.
                Your trades will exit via your IP. Strategy stays on our server.
              </div>
              <div className="flex items-center gap-3 bg-zinc-900 rounded px-3 py-2">
                <Switch data-testid="toggle-proxy-enabled" checked={!!broker.proxy_enabled} onCheckedChange={(v) => { setBroker({ ...broker, proxy_enabled: v }); scheduleBrokerAutosave(); }} />
                <div className="text-xs font-mono">Use my proxy for Dhan API <span className="text-zinc-500">(fill Host+Port+User+Pass first)</span></div>
              </div>
              <Field label="Proxy Type">
                <select data-testid="select-proxy-type" value={broker.proxy_type || "http"} onChange={(e) => { setBroker({ ...broker, proxy_type: e.target.value }); scheduleBrokerAutosave(); }} className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm font-mono text-zinc-200">
                  <option value="http">HTTP</option>
                  <option value="socks5">SOCKS5</option>
                </select>
              </Field>
              <Field label="Proxy Host (your VPS IP)">
                <Input data-testid="input-proxy-host" value={broker.proxy_host || ""} onChange={(e) => { setBroker({ ...broker, proxy_host: e.target.value }); scheduleBrokerAutosave(); }} placeholder="66.23.228.39" className="bg-zinc-900 border-zinc-800 font-mono" />
              </Field>
              <Field label="Proxy Port">
                <Input data-testid="input-proxy-port" type="number" value={broker.proxy_port || ""} onChange={(e) => { setBroker({ ...broker, proxy_port: Number(e.target.value) || 0 }); scheduleBrokerAutosave(); }} placeholder="3128" className="bg-zinc-900 border-zinc-800 font-mono" />
              </Field>
              <Field label="Proxy Username (required if 407)">
                <Input data-testid="input-proxy-user" value={broker.proxy_username || ""} onChange={(e) => { setBroker({ ...broker, proxy_username: e.target.value }); scheduleBrokerAutosave(); }} className="bg-zinc-900 border-zinc-800 font-mono" />
              </Field>
              <Field label="Proxy Password (required if 407)">
                <Input data-testid="input-proxy-pass" type="password" value={broker.proxy_password || ""} onChange={(e) => { setBroker({ ...broker, proxy_password: e.target.value }); scheduleBrokerAutosave(); }} className="bg-zinc-900 border-zinc-800 font-mono" />
              </Field>
              <Button onClick={testProxy} disabled={loading.validate} variant="outline" className="w-full border-amber-700 text-amber-400 hover:bg-amber-950" data-testid="proxy-test-btn">
                Test Proxy (show exit IP)
              </Button>
              <div className="text-[10px] text-zinc-500 font-mono">If Test fails with 407: fill username + password, Save, Test again. Whitelist exit IP in Dhan.</div>
            </div>
          </Card>

{/* Telegram Alerts */}
          <Card className="bg-zinc-950/80 border-zinc-800 p-5">
            <div className={HDR}>📢 Telegram Alerts</div>
            <div className="space-y-3">
              <Field label="Bot Token">
                <Input data-testid="input-tg-token" type={show.tgtoken ? "text" : "password"} value={broker.telegram_bot_token} onChange={(e) => { setBroker({ ...broker, telegram_bot_token: e.target.value }); scheduleBrokerAutosave(); }} placeholder="123456:ABC..." className="bg-zinc-900 border-zinc-800 font-mono" />
              </Field>
              <Field label="Chat ID">
                <Input data-testid="input-tg-chat" value={broker.telegram_chat_id} onChange={(e) => { setBroker({ ...broker, telegram_chat_id: e.target.value }); scheduleBrokerAutosave(); }} placeholder="123456789" className="bg-zinc-900 border-zinc-800 font-mono" />
              </Field>
              <div className="flex items-center gap-3 bg-zinc-900 rounded px-3 py-2">
                <Switch data-testid="toggle-tg-enabled" checked={!!broker.telegram_enabled} onCheckedChange={(v) => { setBroker({ ...broker, telegram_enabled: v }); scheduleBrokerAutosave(); }} />
                <div className="text-xs font-mono">Enable alerts</div>
              </div>
              <Button onClick={testTelegram} data-testid="tg-test-btn" variant="outline" className="w-full border-sky-700 text-sky-400 hover:bg-sky-950">
                Send Test Alert
              </Button>
              <div className="text-[10px] text-zinc-500 font-mono">Get token from @BotFather. Chat ID: msg @userinfobot.</div>
            </div>
          </Card>
        </aside>

        {/* Main */}
        <main className="col-span-9 space-y-5">
          {/* Setup */}
          <section className={SECTION} data-testid="section-setup">
            <div className={HDR}><TrendingUp className="h-3 w-3" /> Antigravity Setup</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">

              <Field label="Exchange">
                <Select value={strategy.exchange} onValueChange={(v) => setStrategy({ ...strategy, exchange: v, exchange_segment: v === "BSE" ? "BSE_FNO" : v === "MCX" ? "MCX_COMM" : "NSE_FNO" })}>
                  <SelectTrigger data-testid="select-exchange" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="NSE">NSE</SelectItem><SelectItem value="BSE">BSE</SelectItem><SelectItem value="MCX">MCX</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Symbol">
                <Select value={strategy.symbol} onValueChange={(v) => {
                  const MCX_SYMS = new Set(["CRUDEOIL","CRUDEOILM","NATURALGAS","NATGASMINI","NATURALGASM","GOLD","SILVER","GOLDM","SILVERM","COPPER","ZINC","LEAD","NICKEL","ALUMINIUM","MENTHAOIL","COTTON"]);
                  const isMcx = MCX_SYMS.has(String(v||"").toUpperCase());
                  setStrategy(clearContractLock({
                    ...strategy,
                    symbol: v,
                    exchange: isMcx ? "MCX" : (strategy.exchange === "MCX" ? "NSE" : strategy.exchange),
                    exchange_segment: isMcx ? "MCX_COMM" : (strategy.exchange === "BSE" || strategy.exchange_segment === "BSE_FNO" ? "BSE_FNO" : "NSE_FNO"),
                  }));
                }}>
                  <SelectTrigger data-testid="select-symbol" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {Object.keys(symbols).filter((k) => symbols[k].exchange === strategy.exchange).map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Side">
                <Select value={strategy.side} onValueChange={(v) => setStrategy({ ...strategy, side: v })}>
                  <SelectTrigger data-testid="select-side" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="BUY">BUY</SelectItem><SelectItem value="SELL">SELL</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Product">
                <Select value={strategy.product_type} onValueChange={(v) => setStrategy({ ...strategy, product_type: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="INTRADAY">INTRADAY</SelectItem><SelectItem value="MARGIN">MARGIN</SelectItem><SelectItem value="CNC">CNC</SelectItem><SelectItem value="MTF">MTF</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Instrument Family">
                <Select value={strategy.instrument_family} onValueChange={(v) => setStrategy({ ...strategy, instrument_family: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Option">Option</SelectItem><SelectItem value="Future">Future</SelectItem><SelectItem value="Equity">Equity</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Expiry">
                <Select value={strategy.expiry_type} onValueChange={(v) => setStrategy(clearContractLock({ ...strategy, expiry_type: v }))}>
                  <SelectTrigger data-testid="select-expiry" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="CURRENT">Current</SelectItem><SelectItem value="NEXT">Next</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Lot Size"><Input data-testid="input-lot-size" type="number" value={strategy.lot_size} onChange={(e) => setStrategy({ ...strategy, lot_size: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono" /></Field>
              <Field label="Quantity (lots)"><Input data-testid="input-qty" disabled={multiWaitMode} title={multiWaitMode ? "Clear Multi Wait box to edit" : ""} type="number" value={strategy.quantity} onChange={(e) => {
                const quantity = Number(e.target.value);
                const qty = Math.max(1, quantity || 1);
                if (strategy.qty_split_mode === "EQUAL") {
                  const q1 = Math.floor(qty / 3);
                  const q2 = Math.floor(qty / 3);
                  const q3 = qty - q1 - q2;
                  setStrategy({
                    ...strategy,
                    quantity,
                    t1_qty_pct: Math.round((q1 / qty) * 1000) / 10,
                    t2_qty_pct: Math.round((q2 / qty) * 1000) / 10,
                    t3_qty_pct: Math.round((q3 / qty) * 1000) / 10,
                  });
                } else {
                  setStrategy({ ...strategy, quantity });
                }
              }} className="bg-zinc-900 border-zinc-800 font-mono" /></Field>
            </div>
          </section>

          {/* Options Config */}
          <section className={SECTION} data-testid="section-options">
            <div className={HDR}>Options Configuration</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              <Field label="Option Type">
                <Select value={strategy.option_type} onValueChange={(v) => setStrategy(clearContractLock({ ...strategy, option_type: v, ...(strategy.signal_enabled && strategy.signal_type === "PRICE_CROSS_UP" ? { signal_watch: v } : {}) }))}>
                  <SelectTrigger data-testid="select-option-type" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="CE">CE (Call)</SelectItem><SelectItem value="PE">PE (Put)</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Moneyness">
                <Select value={strategy.moneyness} onValueChange={(v) => setStrategy(clearContractLock({ ...strategy, moneyness: v }))}>
                  <SelectTrigger data-testid="select-moneyness" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["ATM","OTM1","OTM2","OTM3","ITM1","ITM2","ITM3"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Strike Selection">
                <Select value={strategy.strike_selection} onValueChange={(v) => setStrategy(clearContractLock({ ...strategy, strike_selection: v }))}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="AUTO">Auto by Moneyness</SelectItem><SelectItem value="MANUAL">Manual Strike</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Manual Strike">
                <Input type="number" value={strategy.manual_strike} onChange={(e) => setStrategy(clearContractLock({ ...strategy, manual_strike: Number(e.target.value) }))} disabled={strategy.strike_selection === "AUTO"} className="bg-zinc-900 border-zinc-800 font-mono" />
              </Field>
            </div>
            <div className="mt-4">
              <Button onClick={resolveScrip} disabled={loading.resolve} variant="outline" data-testid="resolve-btn" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
                <RefreshCw className={`h-4 w-4 mr-2 ${loading.resolve ? "animate-spin" : ""}`} /> Resolve via Scrip Master
              </Button>
            </div>
          </section>

          {/* Pricing & Exits — compact */}
          <section className={SECTION} data-testid="section-pricing">
            <div className={HDR}>Pricing & Exits</div>
            <div className="flex flex-wrap items-end gap-2 lg:gap-3">
              <div className="w-[130px] min-w-[120px]">
                <Field label="Entry Type">
                  <Select
                    value={strategy.cond_no_lower_wick_30m ? "MARKET" : strategy.entry_type}
                    onValueChange={(v) => {
                      if (strategy.cond_no_lower_wick_30m) return;
                      const u = String(v).toUpperCase();
                      if (u === "MARKET") {
                        setStrategy({ ...strategy, entry_type: v, entry_price: 0, limit_price: 0 });
                      } else if (u === "WAIT_PRICE") {
                        setStrategy({ ...strategy, entry_type: v, price_wait_enabled: true });
                      } else {
                        setStrategy({ ...strategy, entry_type: v, price_wait_enabled: false });
                      }
                    }}
                    disabled={!!strategy.cond_no_lower_wick_30m}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-800 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LIMIT">LIMIT</SelectItem>
                      <SelectItem value="MARKET">MARKET</SelectItem>
                      <SelectItem value="WAIT_PRICE">WAIT PRICE</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="w-[120px] min-w-[110px]">
                <Field label="Entry Price">
                  <Input
                    data-testid="input-entry-price"
                    type="number"
                    step="0.05"
                    disabled={multiWaitMode || !!strategy.cond_no_lower_wick_30m || (strategy.entry_type || "").toUpperCase() === "MARKET"}
                    title={
                      strategy.cond_no_lower_wick_30m
                        ? "Locked — Special volume Auto MARKET"
                        : multiWaitMode
                        ? "Clear Multi Wait box to edit"
                        : (strategy.entry_type || "").toUpperCase() === "WAIT_PRICE"
                        ? "WAIT PRICE: order only when LTP reaches this price (use Entry band)"
                        : (strategy.entry_type || "").toUpperCase() === "MARKET"
                        ? "MARKET: live LTP used"
                        : ""
                    }
                    value={strategy.cond_no_lower_wick_30m ? 0 : strategy.entry_price}
                    onChange={(e) => {
                      const ep = Number(e.target.value) || 0;
                      const gap = Number(strategy.target_gap || 0);
                      const side = (strategy.side || "BUY").toUpperCase();
                      const patch = { ...strategy, entry_price: ep, limit_price: ep };
                      if (gap > 0 && ep > 0) {
                        if (side === "SELL") {
                          patch.t1 = Number((ep - gap).toFixed(2));
                          patch.t2 = Number((ep - 2 * gap).toFixed(2));
                          patch.t3 = Number((ep - 3 * gap).toFixed(2));
                        } else {
                          patch.t1 = Number((ep + gap).toFixed(2));
                          patch.t2 = Number((ep + 2 * gap).toFixed(2));
                          patch.t3 = Number((ep + 3 * gap).toFixed(2));
                        }
                      }
                      setStrategy(patch);
                    }}
                    className="bg-zinc-900 border-zinc-800 font-mono h-9"
                  />
                </Field>
              </div>
              <div className="w-[90px] min-w-[80px]">
                <Field label="SL (pts)">
                  <Input data-testid="input-sl" type="number" step="0.5" disabled={multiWaitMode || !!strategy.cond_no_lower_wick_30m} value={strategy.sl_points} onChange={(e) => setStrategy({ ...strategy, sl_points: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                </Field>
              </div>
              <div className="w-[90px] min-w-[80px]">
                <Field label="Target Gap">
                  <Input type="number" step="0.5" disabled={multiWaitMode} value={strategy.target_gap} onChange={(e) => {
                    const gap = Number(e.target.value) || 0;
                    const ep = Number(strategy.entry_price || 0);
                    const side = (strategy.side || "BUY").toUpperCase();
                    let t1 = strategy.t1, t2 = strategy.t2, t3 = strategy.t3;
                    if (gap > 0 && ep > 0) {
                      if (side === "SELL") {
                        t1 = Number((ep - gap).toFixed(2));
                        t2 = Number((ep - 2 * gap).toFixed(2));
                        t3 = Number((ep - 3 * gap).toFixed(2));
                      } else {
                        t1 = Number((ep + gap).toFixed(2));
                        t2 = Number((ep + 2 * gap).toFixed(2));
                        t3 = Number((ep + 3 * gap).toFixed(2));
                      }
                    }
                    setStrategy({ ...strategy, target_gap: gap, t1, t2, t3 });
                  }} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                </Field>
              </div>
              <div className="w-[80px] min-w-[70px]">
                <Field label="T1">
                  <Input type="number" step="0.5" value={strategy.t1} onChange={(e) => setStrategy({ ...strategy, t1: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                </Field>
              </div>
              <div className="w-[80px] min-w-[70px]">
                <Field label="T2">
                  <Input type="number" step="0.5" value={strategy.t2} onChange={(e) => setStrategy({ ...strategy, t2: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                </Field>
              </div>
              <div className="w-[80px] min-w-[70px]">
                <Field label="T3">
                  <Input type="number" step="0.5" value={strategy.t3} onChange={(e) => setStrategy({ ...strategy, t3: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                </Field>
              </div>
              <div className="w-[70px] min-w-[60px]">
                <Field label="T1 split">
                  <Input type="number" min="0" value={Math.round((Number(strategy.t1_qty_pct)||0)/100 * Math.max(1, Number(strategy.quantity)||1))} onChange={(e) => {
                    const lots = Math.max(0, Number(e.target.value)||0);
                    const totalQty = Math.max(1, Number(strategy.quantity)||1);
                    const t2Lots = Math.round((Number(strategy.t2_qty_pct)||0)/100 * totalQty);
                    const t3Lots = Math.round((Number(strategy.t3_qty_pct)||0)/100 * totalQty);
                    const newTotal = Math.max(1, lots + t2Lots + t3Lots);
                    setStrategy({
                      ...strategy,
                      quantity: newTotal,
                      qty_split_mode: "PERCENT",
                      t1_qty_pct: Math.round((lots / newTotal) * 1000) / 10,
                      t2_qty_pct: Math.round((t2Lots / newTotal) * 1000) / 10,
                      t3_qty_pct: Math.round((t3Lots / newTotal) * 1000) / 10,
                    });
                  }} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                </Field>
              </div>
              <div className="w-[70px] min-w-[60px]">
                <Field label="T2 split">
                  <Input type="number" min="0" value={Math.round((Number(strategy.t2_qty_pct)||0)/100 * Math.max(1, Number(strategy.quantity)||1))} onChange={(e) => {
                    const lots = Math.max(0, Number(e.target.value)||0);
                    const totalQty = Math.max(1, Number(strategy.quantity)||1);
                    const t1Lots = Math.round((Number(strategy.t1_qty_pct)||0)/100 * totalQty);
                    const t3Lots = Math.round((Number(strategy.t3_qty_pct)||0)/100 * totalQty);
                    const newTotal = Math.max(1, t1Lots + lots + t3Lots);
                    setStrategy({
                      ...strategy,
                      quantity: newTotal,
                      qty_split_mode: "PERCENT",
                      t1_qty_pct: Math.round((t1Lots / newTotal) * 1000) / 10,
                      t2_qty_pct: Math.round((lots / newTotal) * 1000) / 10,
                      t3_qty_pct: Math.round((t3Lots / newTotal) * 1000) / 10,
                    });
                  }} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                </Field>
              </div>
              <div className="w-[70px] min-w-[60px]">
                <Field label="T3 split">
                  <Input type="number" min="0" value={Math.round((Number(strategy.t3_qty_pct)||0)/100 * Math.max(1, Number(strategy.quantity)||1))} onChange={(e) => {
                    const lots = Math.max(0, Number(e.target.value)||0);
                    const totalQty = Math.max(1, Number(strategy.quantity)||1);
                    const t1Lots = Math.round((Number(strategy.t1_qty_pct)||0)/100 * totalQty);
                    const t2Lots = Math.round((Number(strategy.t2_qty_pct)||0)/100 * totalQty);
                    const newTotal = Math.max(1, t1Lots + t2Lots + lots);
                    setStrategy({
                      ...strategy,
                      quantity: newTotal,
                      qty_split_mode: "PERCENT",
                      t1_qty_pct: Math.round((t1Lots / newTotal) * 1000) / 10,
                      t2_qty_pct: Math.round((t2Lots / newTotal) * 1000) / 10,
                      t3_qty_pct: Math.round((lots / newTotal) * 1000) / 10,
                    });
                  }} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                </Field>
              </div>
            </div>
            {(strategy.entry_type || "").toUpperCase() === "WAIT_PRICE" && (
              <div className="text-[10px] text-sky-400/90 mt-2 font-mono">
                WAIT PRICE: buys only when LTP is in Entry → Entry+band. Set Entry band (Qty Split row) for fast markets (Sensex / BankNifty / Crude). Will not buy early.
              </div>
            )}
          </section>

          {/* Qty Split & Re-Entry — compact 2-row layout */}
          <section className={SECTION} data-testid="section-qty-reentry">
            <div className={HDR}>Qty Split & Re-Entry</div>
            {/* Row 1 */}
            <div className="flex flex-wrap items-end gap-2 lg:gap-3">
              <div className="w-[140px] min-w-[120px] flex-shrink-0">
                <Field label="Qty Split">
                  <Select value={strategy.qty_split_mode} onValueChange={(v) => {
                    const qty = Math.max(1, Number(strategy.quantity) || 1);
                    if (v === "EQUAL") {
                      const q1 = Math.floor(qty / 3);
                      const q2 = Math.floor(qty / 3);
                      const q3 = qty - q1 - q2;
                      setStrategy({
                        ...strategy,
                        qty_split_mode: v,
                        t1_qty_pct: Math.round((q1 / qty) * 1000) / 10,
                        t2_qty_pct: Math.round((q2 / qty) * 1000) / 10,
                        t3_qty_pct: Math.round((q3 / qty) * 1000) / 10,
                      });
                    } else {
                      setStrategy({ ...strategy, qty_split_mode: v, t1_qty_pct: 25, t2_qty_pct: 25, t3_qty_pct: 50 });
                    }
                  }}>
                    <SelectTrigger data-testid="select-qty-split" className="bg-zinc-900 border-zinc-800 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PERCENT">Share Based</SelectItem>
                      <SelectItem value="EQUAL">Equal Slice</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="flex items-center gap-2 bg-zinc-900 rounded px-2.5 h-9 border border-zinc-800">
                <Switch data-testid="toggle-reentry-sl" checked={strategy.re_entry_on_sl} onCheckedChange={(v) => setStrategy({ ...strategy, re_entry_on_sl: v })} />
                <span className="text-[11px] font-mono text-zinc-300 whitespace-nowrap">Re-Entry SL</span>
              </div>
              <div className="flex items-center gap-2 bg-zinc-900 rounded px-2.5 h-9 border border-zinc-800">
                <Switch checked={strategy.re_entry_on_target} onCheckedChange={(v) => setStrategy({ ...strategy, re_entry_on_target: v })} />
                <span className="text-[11px] font-mono text-zinc-300 whitespace-nowrap">Re-Entry Target</span>
              </div>
              <div className="w-[100px] min-w-[90px]">
                <Field label="Max Re-entry">
                  <Input data-testid="input-max-reentries" type="number" step="1" value={strategy.max_reentries} onChange={(e) => setStrategy({ ...strategy, max_reentries: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono h-9" title="0=off, -1=unlimited" />
                </Field>
              </div>
              <div className="w-[90px] min-w-[80px]">
                <Field label="Grace (sec)">
                  <Input data-testid="input-reentry-grace" type="number" step="1" min="0" placeholder="0" value={strategy.reentry_grace_seconds} onChange={(e) => setStrategy({ ...strategy, reentry_grace_seconds: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                </Field>
              </div>
              <div className="w-[90px] min-w-[80px]">
                <Field label="Entry band">
                  <Input data-testid="input-entry-band" type="number" step="0.5" min="0" max="10" disabled={!!strategy.cond_no_lower_wick_30m} title="0=exact, max 10 pts" value={strategy.entry_band_points ?? 0} onChange={(e) => { const v = Math.min(10, Math.max(0, Number(e.target.value))); setStrategy({ ...strategy, entry_band_points: v }); }} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                </Field>
              </div>
            </div>
            {/* Row 2 — counter only */}
            <div className="flex items-center gap-2 mt-2">
              <span className="text-[11px] font-mono text-zinc-500">Used:</span>
              <span className="text-[11px] font-mono text-amber-400">{strategy.reentry_count || 0}</span>
              <span className="text-[11px] font-mono text-zinc-500">{strategy.max_reentries > 0 ? `/ ${strategy.max_reentries}` : "/ ∞"}</span>
              <Button data-testid="reset-reentry-btn" size="sm" variant="outline" className="h-6 px-2 text-[10px] border-zinc-700 ml-1" onClick={resetReentry} disabled={!strategy.id || strategy.id.startsWith("draft-")}>Reset</Button>
            </div>
          </section>

          {/* Advanced Risk — compact 2-row layout */}
          <section className={SECTION} data-testid="section-advanced">
            <div className={HDR}>Advanced Risk</div>
            {/* Row 1: trailing + MTM + times */}
            <div className="flex flex-wrap items-end gap-2 lg:gap-3">
              <div className="w-[160px] min-w-[140px]">
                <Field label="Trailing Mode">
                  <Select value={strategy.trailing_mode || "COMMON"} onValueChange={(v) => setStrategy({ ...strategy, trailing_mode: v })} disabled={!!strategy.cond_no_lower_wick_30m}>
                    <SelectTrigger data-testid="select-trailing-mode" className="bg-zinc-900 border-zinc-800 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">NONE — Fixed SL</SelectItem>
                      <SelectItem value="COMMON">COMMON — One trail</SelectItem>
                      <SelectItem value="STEP">STEP — T1→BE…</SelectItem>
                      <SelectItem value="INDIVIDUAL">INDIVIDUAL</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              {(strategy.trailing_mode || "COMMON") === "COMMON" && (
                <div className="w-[100px] min-w-[90px]">
                  <Field label="Trail SL (pts)">
                    <Input type="number" step="0.5" disabled={multiWaitMode || !!strategy.cond_no_lower_wick_30m} value={strategy.cond_no_lower_wick_30m ? (strategy.candle_trail_points ?? strategy.trailing_sl) : strategy.trailing_sl} onChange={(e) => setStrategy({ ...strategy, trailing_sl: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                  </Field>
                </div>
              )}
              {(strategy.trailing_mode || "COMMON") === "INDIVIDUAL" && (
                <>
                  <div className="w-[80px]"><Field label="Trail T1"><Input type="number" step="0.5" value={strategy.trail_t1 || 0} onChange={(e) => setStrategy({ ...strategy, trail_t1: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono h-9" /></Field></div>
                  <div className="w-[80px]"><Field label="Trail T2"><Input type="number" step="0.5" value={strategy.trail_t2 || 0} onChange={(e) => setStrategy({ ...strategy, trail_t2: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono h-9" /></Field></div>
                  <div className="w-[80px]"><Field label="Trail T3"><Input type="number" step="0.5" value={strategy.trail_t3 || 0} onChange={(e) => setStrategy({ ...strategy, trail_t3: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono h-9" /></Field></div>
                </>
              )}
              <div className="flex items-center gap-2 bg-zinc-900 rounded px-2.5 h-9 border border-zinc-800">
                <Switch checked={!!strategy.target_trail_enabled} onCheckedChange={(v) => setStrategy({ ...strategy, target_trail_enabled: v })} />
                <span className="text-[11px] font-mono text-zinc-300 whitespace-nowrap">Target Trail</span>
              </div>
              {strategy.target_trail_enabled && (
                <div className="w-[100px] min-w-[90px]">
                  <Field label="Trail Jump">
                    <Input type="number" step="0.5" min="0" value={strategy.target_trail_jump || 0} onChange={(e) => setStrategy({ ...strategy, target_trail_jump: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                  </Field>
                </div>
              )}
              <div className="w-[110px] min-w-[100px]">
                <Field label="MTM SL (₹)">
                  <Input type="number" value={strategy.strategy_mtm_sl} onChange={(e) => setStrategy({ ...strategy, strategy_mtm_sl: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono h-9" title="Exit strategy if loss reaches this amount" />
                </Field>
              </div>
              <div className="w-[110px] min-w-[100px]">
                <Field label="MTM Target (₹)">
                  <Input type="number" value={strategy.strategy_mtm_target} onChange={(e) => setStrategy({ ...strategy, strategy_mtm_target: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono h-9" title="Exit strategy if profit reaches this amount" />
                </Field>
              </div>
              <div className="w-[120px] min-w-[110px]">
                <Field label="Entry Time">
                  <Input data-testid="input-entry-time" type="text" placeholder="09:15:00" value={strategy.entry_time} onChange={(e) => setStrategy({ ...strategy, entry_time: e.target.value })} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                </Field>
              </div>
              <div className="w-[100px] min-w-[90px]">
                <Field label="Exit Time">
                  <Input data-testid="input-exit-time" type="text" placeholder="23:15" value={strategy.exit_time} onChange={(e) => setStrategy({ ...strategy, exit_time: e.target.value })} className="bg-zinc-900 border-zinc-800 font-mono h-9" />
                </Field>
              </div>
            </div>
            {/* Row 2: When to Buy */}
            <div className="flex flex-wrap items-end gap-2 lg:gap-3 mt-2">
              <div className="w-[240px] min-w-[200px]">
                <Field label="When to Buy">
                  <Select value={strategy.entry_filter} onValueChange={(v) => setStrategy({ ...strategy, entry_filter: v })}>
                    <SelectTrigger data-testid="select-entry-filter" className="bg-zinc-900 border-zinc-800 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">Buy immediately on Deploy</SelectItem>
                      <SelectItem value="UPTICK_ONLY">Buy once when price is rising</SelectItem>
                      <SelectItem value="DOWNTICK_ONLY">Buy once when price is falling</SelectItem>
                      <SelectItem value="UPTICK_MULTI">Buy each time price is rising</SelectItem>
                      <SelectItem value="DOWNTICK_MULTI">Buy each time price is falling</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="text-[10px] text-zinc-500 self-center max-w-md leading-snug">
                <b className="text-sky-400/90">Rising</b> = buy when price goes up. Not when only falling. MTM SL/Target exit the strategy after entry when loss/profit hits.
              </div>
            </div>
          </section>

          <section className={`${SECTION}${HIDE_PARTNER_SECTIONS ? " hidden" : ""}`} data-testid="section-signals" aria-hidden={HIDE_PARTNER_SECTIONS}>
            <div className={HDR}>Entry Signals</div>
              <div className="p-3 rounded-lg border border-zinc-800 bg-zinc-900/40 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div>
                      <div className="text-sm font-medium text-zinc-200">Buy Entry Indicators → Auto MARKET</div>
                      <div className="text-[10px] text-zinc-500">ON conditions fire signal · ALL = every must pass · ANY = one enough</div>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Select value={strategy.cond_mode || "ANY"} onValueChange={(v) => setStrategy({ ...strategy, cond_mode: v })}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-800 w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">ALL (AND)</SelectItem>
                        <SelectItem value="ANY">ANY (OR)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={strategy.cond_on || "OPTION"} onValueChange={(v) => setStrategy({ ...strategy, cond_on: v })}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-800 w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UNDERLYING">Underlying</SelectItem>
                        <SelectItem value="OPTION">Option LTP</SelectItem>
                        <SelectItem value="BOTH">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold pt-1">A · Filters</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="flex items-center justify-between gap-2 p-2 rounded border border-zinc-800">
                    <span className="text-xs text-zinc-300">VWAP Today+Prev (5m)</span>
                    <Switch checked={!!strategy.cond_vwap} onCheckedChange={(v) => setStrategy({ ...strategy, cond_vwap: v })} />
                  </div>
                  <div className="flex items-center justify-between gap-2 p-2 rounded border border-zinc-800">
                    <span className="text-xs text-zinc-300">Confirm Candle</span>
                    <Switch checked={!!strategy.cond_confirm_candle} onCheckedChange={(v) => setStrategy({ ...strategy, cond_confirm_candle: v })} />
                  </div>
                  <div className="flex items-center justify-between gap-2 p-2 rounded border border-zinc-800">
                    <span className="text-xs text-zinc-300">Volume > Avg</span>
                    <Switch checked={!!strategy.cond_volume_filter} onCheckedChange={(v) => setStrategy({ ...strategy, cond_volume_filter: v })} />
                  </div>

                </div>
                <div className="text-[10px] uppercase tracking-wide text-emerald-500/80 font-semibold pt-2">B · Special volume (standalone)</div>
                <div className="grid grid-cols-1 gap-3">
                  <div className="p-3 rounded-lg border border-emerald-900/50 bg-emerald-950/20 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-emerald-200 font-medium">Special volume candle (works alone)</span>
                      <Switch checked={!!strategy.cond_no_lower_wick_30m} onCheckedChange={(v) => setStrategy({
                        ...strategy,
                        cond_no_lower_wick_30m: v,
                        signal_wait_enabled: v ? true : strategy.signal_wait_enabled,
                        // Special volume = signal only → always MARKET, no manual entry price
                        ...(v ? { entry_type: "MARKET", entry_price: 0, limit_price: 0 } : {}),
                      })} />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div>
                        <span className="text-[9px] text-zinc-500">TF (min)</span>
                        <select
                          value={strategy.no_lower_wick_tf ?? 30}
                          onChange={(e) => setStrategy({ ...strategy, no_lower_wick_tf: Number(e.target.value) })}
                          className="h-7 w-full rounded border border-zinc-800 bg-zinc-900 px-1 font-mono text-xs text-emerald-200"
                        >
                          <option value={5}>5m</option>
                          <option value={15}>15m</option>
                          <option value={30}>30m</option>
                        </select>
                      </div>
                      <div>
                        <span className="text-[9px] text-zinc-400" title="Max lower wick allowed on closed candle (min 0.05 tick). 0 still uses 0.05.">Wick tol (pts)</span>
                        <Input type="number" step="0.01" min="0" value={strategy.no_lower_wick_tolerance ?? 0.05} onChange={(e) => setStrategy({ ...strategy, no_lower_wick_tolerance: Number(e.target.value) })} className="h-7 bg-zinc-900 border-zinc-800 font-mono text-xs" placeholder="0" />
                      </div>
                      <div>
                        <span className="text-[9px] text-zinc-500">SL mode</span>
                        <select
                          value={strategy.special_vol_sl_mode || "POINTS"}
                          onChange={(e) => setStrategy({ ...strategy, special_vol_sl_mode: e.target.value })}
                          className="h-7 w-full rounded border border-zinc-800 bg-zinc-900 px-1 font-mono text-xs text-emerald-200"
                        >
                          <option value="POINTS">SL pts</option>
                          <option value="PREV_LOW">Candle low</option>
                        </select>
                      </div>
                      <div>
                        <span className="text-[9px] text-zinc-500">{(strategy.special_vol_sl_mode || "POINTS") === "PREV_LOW" ? "Buf below low" : "SL pts"}</span>
                        {(strategy.special_vol_sl_mode || "POINTS") === "PREV_LOW" ? (
                          <Input type="number" step="0.05" min="0" value={strategy.special_vol_sl_buffer ?? 0} onChange={(e) => setStrategy({ ...strategy, special_vol_sl_buffer: Number(e.target.value) })} className="h-7 bg-zinc-900 border-zinc-800 font-mono text-xs" placeholder="0" />
                        ) : (
                          <Input type="number" step="0.5" min="0" value={strategy.sl_points ?? 4} onChange={(e) => setStrategy({ ...strategy, sl_points: Number(e.target.value) })} className="h-7 bg-zinc-900 border-zinc-800 font-mono text-xs" placeholder="4" />
                        )}
                      </div>
                      <div>
                        <span className="text-[9px] text-zinc-400" title="After entry: stop-loss trails price by this many points (0 = no trail)">SL trail pts</span>
                        <Input type="number" step="0.5" min="0" value={strategy.candle_trail_points ?? strategy.sl_points ?? 4} onChange={(e) => setStrategy({ ...strategy, candle_trail_points: Number(e.target.value) })} className="h-7 bg-zinc-900 border-zinc-800 font-mono text-xs" placeholder="4" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div>
                        <span className="text-[9px] text-zinc-400" title="Take-profit distance from entry in points">Target pts</span>
                        <Input type="number" step="0.5" min="0" value={strategy.special_vol_target_pts ?? 10} onChange={(e) => setStrategy({ ...strategy, special_vol_target_pts: Number(e.target.value) })} className="h-7 bg-zinc-900 border-zinc-800 font-mono text-xs" placeholder="10" />
                      </div>
                      <div>
                        <span className="text-[9px] text-zinc-400" title="Move take-profit in steps as price runs (0 = fixed target)">Tgt trail pts</span>
                        <Input type="number" step="0.5" min="0" value={strategy.special_vol_target_trail_pts ?? 0} onChange={(e) => setStrategy({ ...strategy, special_vol_target_trail_pts: Number(e.target.value) })} className="h-7 bg-zinc-900 border-zinc-800 font-mono text-xs" placeholder="0" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <span className="text-[10px] text-zinc-400">SL trail after entry (not a new candle check)</span>
                      <Switch checked={!!strategy.trail_sl_from_next_open} onCheckedChange={(v) => setStrategy({ ...strategy, trail_sl_from_next_open: v })} />
                    </div>
                    <div className="text-[10px] text-emerald-200/90 leading-relaxed space-y-0.5 border-t border-emerald-900/40 pt-2 mt-1">
                      <div className="font-semibold text-emerald-300">How to use</div>
                      <div>1. Choose TF (5 / 15 / 30) · Wick tol min 0.05 · turn Switch ON</div>
                      <div>2. SL mode: <b>SL pts</b> = fixed points · <b>Candle low</b> = stop under signal candle low</div>
                      <div>3. <b>SL trail pts</b> = after entry, SL follows price by N points (0 = fixed SL)</div>
                      <div>4. <b>Target pts</b> = profit target gap from entry · <b>Tgt trail pts</b> = trail that target (0 = fixed)</div>
                      <div>5. Press <b>Auto MARKET / Signal Wait</b> — status must stay SIGNAL_WAIT</div>
                      <div className="text-zinc-400">Entry signal = <b>last closed</b> candle only (not the live bar). Green + lower wick ≤ tol → then buy.</div>
                    </div>
                  </div>

                </div>
                <div className="text-[10px] uppercase tracking-wide text-sky-500/80 font-semibold pt-2">C · Trend crosses</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="p-2 rounded border border-zinc-800 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-300">EMA cross</span>
                      <Switch checked={!!strategy.cond_ema} onCheckedChange={(v) => setStrategy({ ...strategy, cond_ema: v })} />
                    </div>
                    <div className="flex gap-1">
                      <Input type="number" min="1" value={strategy.cond_ema_period || 20} onChange={(e) => setStrategy({ ...strategy, cond_ema_period: Number(e.target.value) })} className="h-7 bg-zinc-900 border-zinc-800 font-mono text-xs" placeholder="Period" />
                      <select
                        className="h-7 rounded border border-zinc-700 bg-zinc-900 px-1 text-[11px] font-mono text-zinc-200"
                        value={String(strategy.cond_ema_tf || 5)}
                        onChange={(e) => setStrategy({ ...strategy, cond_ema_tf: Number(e.target.value) })}
                      >
                        <option value="5">5m</option>
                        <option value="15">15m</option>
                      </select>
                    </div>
                  </div>
                  <div className="p-2 rounded border border-zinc-800 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-300">RSI above</span>
                      <Switch checked={!!strategy.cond_rsi} onCheckedChange={(v) => setStrategy({ ...strategy, cond_rsi: v })} />
                    </div>
                    <div className="flex gap-1">
                      <Input type="number" min="1" value={strategy.cond_rsi_period || 14} onChange={(e) => setStrategy({ ...strategy, cond_rsi_period: Number(e.target.value) })} className="h-7 bg-zinc-900 border-zinc-800 font-mono text-xs" placeholder="Period" />
                      <Input type="number" value={strategy.cond_rsi_level || 50} onChange={(e) => setStrategy({ ...strategy, cond_rsi_level: Number(e.target.value) })} className="h-7 bg-zinc-900 border-zinc-800 font-mono text-xs" placeholder="Level" />
                      <select
                        className="h-7 rounded border border-zinc-700 bg-zinc-900 px-1 text-[11px] font-mono text-zinc-200"
                        value={String(strategy.cond_rsi_tf || 5)}
                        onChange={(e) => setStrategy({ ...strategy, cond_rsi_tf: Number(e.target.value) })}
                      >
                        <option value="5">5m</option>
                        <option value="15">15m</option>
                      </select>
                    </div>
                  </div>
                  <div className="p-2 rounded border border-zinc-800 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-300">BB 1H mid cross</span>
                      <Switch checked={!!strategy.cond_bb} onCheckedChange={(v) => setStrategy({ ...strategy, cond_bb: v })} />
                    </div>
                    <div className="flex gap-1">
                      <Input type="number" min="1" value={strategy.cond_bb_period || 15} onChange={(e) => setStrategy({ ...strategy, cond_bb_period: Number(e.target.value) })} className="h-7 bg-zinc-900 border-zinc-800 font-mono text-xs" placeholder="Len" />
                      <Input type="number" step="0.5" min="0.5" value={strategy.cond_bb_stddev || 2} onChange={(e) => setStrategy({ ...strategy, cond_bb_stddev: Number(e.target.value) })} className="h-7 bg-zinc-900 border-zinc-800 font-mono text-xs" placeholder="Std" />
                    </div>
                  </div>
                  <div className="p-2 rounded border border-orange-900/50 bg-orange-950/20 space-y-1 col-span-1 sm:col-span-2 lg:col-span-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-orange-300">Special EMA</span>
                      <Switch checked={!!strategy.cond_sqrt_ema} onCheckedChange={(v) => setStrategy({ ...strategy, cond_sqrt_ema: v })} />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-7 rounded border border-zinc-800 bg-zinc-950 px-2 flex items-center font-mono text-xs text-orange-300" data-testid="sqrt-ema-value">
                        {sqrtEmaInfo?.dyn_length != null ? `Length ${sqrtEmaInfo.dyn_length}` : "—"}
                      </div>
                      <Input type="number" step="0.5" min="0" title="Gap % max (0=off)" value={strategy.cond_sqrt_ema_gap_pct ?? 5} onChange={(e) => setStrategy({ ...strategy, cond_sqrt_ema_gap_pct: Number(e.target.value) })} className="h-7 w-16 bg-zinc-900 border-zinc-800 font-mono text-xs" placeholder="Gap%" />
                    </div>
                    <div className="text-[10px] text-orange-200/90 leading-relaxed space-y-0.5 border-t border-orange-900/40 pt-2 mt-1">
                      <div className="font-semibold text-orange-300">How to use</div>
                      <div>1. Turn Special EMA ON</div>
                      <div>2. Length is calculated automatically</div>
                      <div>3. Optional Gap% = max % above EMA (0 = no limit)</div>
                      <div>4. Press Auto MARKET / Signal Wait</div>
                      <div className="text-zinc-400">Buy when price crosses above Special EMA (was below, now above)</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-full grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                <div className="p-2 rounded border border-zinc-800 flex items-center justify-between gap-1">
                  <span className="text-xs text-zinc-300">EMA 50 cross</span>
                  <Switch checked={!!strategy.cond_ema50} onCheckedChange={(v) => setStrategy({ ...strategy, cond_ema50: v })} />
                  <select
                    className="h-7 rounded border border-zinc-700 bg-zinc-900 px-1 text-[11px] font-mono text-zinc-200"
                    value={String(strategy.cond_ema50_tf || 5)}
                    onChange={(e) => setStrategy({ ...strategy, cond_ema50_tf: Number(e.target.value) })}
                    title="Timeframe"
                  >
                    <option value="5">5m</option>
                    <option value="15">15m</option>
                  </select>
                </div>
                <div className="p-2 rounded border border-zinc-800 flex items-center justify-between gap-1">
                  <span className="text-xs text-zinc-300">EMA 200 cross</span>
                  <Switch checked={!!strategy.cond_ema200} onCheckedChange={(v) => setStrategy({ ...strategy, cond_ema200: v })} />
                  <select
                    className="h-7 rounded border border-zinc-700 bg-zinc-900 px-1 text-[11px] font-mono text-zinc-200"
                    value={String(strategy.cond_ema200_tf || 5)}
                    onChange={(e) => setStrategy({ ...strategy, cond_ema200_tf: Number(e.target.value) })}
                    title="Timeframe"
                  >
                    <option value="5">5m</option>
                    <option value="15">15m</option>
                  </select>
                </div>
                <div className="p-2 rounded border border-emerald-900/40 space-y-1 col-span-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-emerald-300" title="3-candle bullish Fair Value Gap on 5m. Fires when LTP is inside the newest unfilled gap.">Bullish FVG retest (5m)</span>
                    <Switch checked={!!strategy.cond_fvg_bullish} onCheckedChange={(v) => setStrategy({ ...strategy, cond_fvg_bullish: v })} />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] text-zinc-500" title="Place stop under the FVG bottom when this signal fires">SL under FVG bottom</span>
                    <Switch checked={!!strategy.cond_fvg_sl_special} onCheckedChange={(v) => setStrategy({ ...strategy, cond_fvg_sl_special: v })} />
                    <Input type="number" step="0.5" value={strategy.cond_fvg_sl_buffer || 0} onChange={(e) => setStrategy({ ...strategy, cond_fvg_sl_buffer: Number(e.target.value) })} className="h-7 w-20 bg-zinc-900 border-zinc-800 font-mono text-xs" placeholder="buf" />
                  </div>
                  <div className="text-[9px] text-zinc-500 leading-snug">Needs LTP inside latest unfilled 5m gap (3-candle FVG: low of candle &gt; high of candle 2 bars earlier).</div>
                </div>
              </div>
              
              <div className="col-span-full p-3 rounded-lg border border-indigo-900/40 bg-indigo-950/20 space-y-3">
                <div className="flex items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-zinc-200">Option Auto Signal</div>
                    <div className="text-[10px] text-zinc-500">Separate from Buy Entry Indicators. Pick one index below — orders go only to that index.</div>
                  </div>
                  <Switch checked={!!strategy.signal_enabled} onCheckedChange={(v) => setStrategy({ ...strategy, signal_enabled: v })} />
                </div>

                {strategy.signal_enabled && (
                  <div className="space-y-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1.5">Step 1 — Select index</div>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { sym: "NIFTY", exch: "NSE", seg: "NSE_FNO" },
                          { sym: "BANKNIFTY", exch: "NSE", seg: "NSE_FNO" },
                          { sym: "SENSEX", exch: "BSE", seg: "BSE_FNO" },
                        ].map((x) => {
                          const active = (strategy.symbol || "").toUpperCase() === x.sym;
                          return (
                            <button
                              key={x.sym}
                              type="button"
                              onClick={() => setStrategy({
                                ...strategy,
                                symbol: x.sym,
                                exchange: x.exch,
                                exchange_segment: x.seg,
                                instrument_family: "Option",
                                signal_type: strategy.signal_type === "NONE" ? "PRICE_CROSS_UP" : (strategy.signal_type || "PRICE_CROSS_UP"),
                                moneyness: strategy.moneyness || "ITM2",
                              })}
                              className={`rounded-md border px-3 py-2.5 text-left transition ${
                                active
                                  ? "border-amber-500/70 bg-amber-500/15 ring-1 ring-amber-500/40"
                                  : "border-zinc-800 bg-zinc-900/50 hover:border-zinc-600"
                              }`}
                            >
                              <div className={`text-sm font-semibold ${active ? "text-amber-300" : "text-zinc-200"}`}>{x.sym}</div>
                              <div className="text-[10px] text-zinc-500 font-mono">{x.seg}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="rounded-md border border-amber-800/50 bg-amber-950/30 px-3 py-2 font-mono text-xs text-amber-100">
                      <span className="text-amber-400/80">TRADING ON → </span>
                      <span className="font-bold text-amber-300">{(strategy.symbol || "—").toUpperCase()}</span>
                      <span className="text-zinc-400"> · </span>
                      <span>
                        {(String(strategy.strike_selection || "").toUpperCase() === "MANUAL" && Number(strategy.manual_strike) > 0)
                          ? ("Strike " + strategy.manual_strike)
                          : (strategy.moneyness || "ITM2")}
                      </span>
                      <span className="text-zinc-400"> · </span>
                      <span>
                        {(strategy.signal_watch || "BOTH") === "BOTH"
                          ? "Both CE + PE"
                          : (strategy.signal_watch || "CE") === "CE"
                            ? "CE only"
                            : "PE only"}
                      </span>
                      <div className="text-[10px] text-zinc-500 mt-1">
                        This also updates the Symbol field above. Orders will only use the selected index.
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Step 2 — Signal type">
                        <Select value={strategy.signal_type || "PRICE_CROSS_UP"} onValueChange={(v) => setStrategy({ ...strategy, signal_type: v })}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PRICE_CROSS_UP">Price cross above level → BUY</SelectItem>
                            <SelectItem value="BB_MID_CROSS_1H">1H BB middle cross → buy next candle</SelectItem>
                            <SelectItem value="NONE">None</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Step 3 — Watch CE / PE">
                        <Select value={strategy.signal_watch || "BOTH"} onValueChange={(v) => setStrategy({ ...strategy, signal_watch: v, ...(v === "CE" || v === "PE" ? { option_type: v } : {}) })}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="BOTH">Both CE + PE</SelectItem>
                            <SelectItem value="CE">CE only</SelectItem>
                            <SelectItem value="PE">PE only</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Strike mode">
                        <Select
                          value={(String(strategy.strike_selection || "AUTO").toUpperCase() === "MANUAL" && Number(strategy.manual_strike) > 0) ? "MANUAL" : "AUTO"}
                          onValueChange={(v) => setStrategy({
                            ...strategy,
                            strike_selection: v,
                            manual_strike: v === "AUTO" ? 0 : (strategy.manual_strike || 0),
                          })}
                        >
                          <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AUTO">Auto (ATM / ITM / OTM)</SelectItem>
                            <SelectItem value="MANUAL">Manual fixed strike</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      {(String(strategy.strike_selection || "").toUpperCase() === "MANUAL") ? (
                        <Field label="Manual strike (same for CE & PE)">
                          <Input type="number" step="1" className="bg-zinc-900 border-zinc-800"
                            placeholder="e.g. 25600"
                            value={strategy.manual_strike || ""}
                            onChange={(e) => setStrategy({
                              ...strategy,
                              strike_selection: "MANUAL",
                              manual_strike: parseFloat(e.target.value) || 0,
                            })} />
                        </Field>
                      ) : (
                        <Field label="Moneyness (auto strike)">
                          <Select value={strategy.moneyness || "ITM2"} onValueChange={(v) => setStrategy({ ...strategy, moneyness: v, strike_selection: "AUTO" })}>
                            <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ATM">ATM</SelectItem>
                              <SelectItem value="ITM1">ITM1</SelectItem>
                              <SelectItem value="ITM2">ITM2</SelectItem>
                              <SelectItem value="ITM3">ITM3</SelectItem>
                              <SelectItem value="OTM1">OTM1</SelectItem>
                              <SelectItem value="OTM2">OTM2</SelectItem>
                            </SelectContent>
                          </Select>
                        </Field>
                      )}
                      <Field label="Max trades (all levels)">
                        <Input type="number" step="1" className="bg-zinc-900 border-zinc-800"
                          value={strategy.signal_max_fires ?? 5}
                          onChange={(e) => setStrategy({ ...strategy, signal_max_fires: parseInt(e.target.value) || 0 })} />
                      </Field>
                    </div>

                    {(strategy.signal_type === "PRICE_CROSS_UP") && (
                      <div className="space-y-2">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                          Step 4 — Price levels for {(strategy.symbol || "INDEX").toUpperCase()} option premium
                        </div>
                        <div className="text-[10px] text-zinc-500 font-mono">
                          Trades: {strategy.signal_fire_count || 0} / {strategy.signal_max_fires ?? 5} · When price goes below level, then crosses above → BUY
                        </div>
                        {(strategy.signal_price_levels && strategy.signal_price_levels.length
                          ? strategy.signal_price_levels
                          : [
                              { level: 66, target: 74, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                              { level: 86, target: 99, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                              { level: 116, target: 129, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                            ]
                        ).map((row, idx) => (
                                                    <div key={idx} className="space-y-1.5 rounded-md border border-zinc-800 bg-zinc-900/40 p-2">
                            <div className="text-[10px] text-zinc-400 font-mono">Level {idx + 1}</div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                            <Field label="Buy above (premium)">
                              <Input type="number" step="0.5" className="bg-zinc-900 border-zinc-800"
                                value={row.level ?? ""}
                                onChange={(e) => {
                                  const base = strategy.signal_price_levels || [
                                    { level: 66, target: 74, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 86, target: 99, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 116, target: 129, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                  ];
                                  const levels = [...base];
                                  levels[idx] = { ...levels[idx], level: parseFloat(e.target.value) || 0 };
                                  setStrategy({ ...strategy, signal_price_levels: levels });
                                }} />
                            </Field>

                            <Field label="Target">
                              <Input type="number" step="0.5" className="bg-zinc-900 border-zinc-800"
                                value={row.target ?? ""}
                                onChange={(e) => {
                                  const base = strategy.signal_price_levels || [
                                    { level: 66, target: 74, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 86, target: 99, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 116, target: 129, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                  ];
                                  const levels = [...base];
                                  levels[idx] = { ...levels[idx], target: parseFloat(e.target.value) || 0 };
                                  setStrategy({ ...strategy, signal_price_levels: levels });
                                }} />
                            </Field>

                            <Field label="SL pts">
                              <Input type="number" step="0.5" className="bg-zinc-900 border-zinc-800"
                                value={row.sl_points ?? 2}
                                onChange={(e) => {
                                  const base = strategy.signal_price_levels || [
                                    { level: 66, target: 74, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 86, target: 99, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 116, target: 129, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                  ];
                                  const levels = [...base];
                                  levels[idx] = { ...levels[idx], sl_points: parseFloat(e.target.value) || 0 };
                                  setStrategy({ ...strategy, signal_price_levels: levels });
                                }} />
                            </Field>

                            <Field label="Lot (qty)">
                              <Input type="number" step="1" className="bg-zinc-900 border-zinc-800"
                                value={row.quantity ?? 1}
                                onChange={(e) => {
                                  const base = strategy.signal_price_levels || [
                                    { level: 66, target: 74, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 86, target: 99, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 116, target: 129, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                  ];
                                  const levels = [...base];
                                  levels[idx] = { ...levels[idx], quantity: parseInt(e.target.value) || 1 };
                                  setStrategy({ ...strategy, signal_price_levels: levels });
                                }} />
                            </Field>

                            <Field label="Trail pts (0=off)">
                              <Input type="number" step="0.5" className="bg-zinc-900 border-zinc-800"
                                value={row.trailing_sl ?? 0}
                                onChange={(e) => {
                                  const base = strategy.signal_price_levels || [
                                    { level: 66, target: 74, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 86, target: 99, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 116, target: 129, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                  ];
                                  const levels = [...base];
                                  levels[idx] = { ...levels[idx], trailing_sl: parseFloat(e.target.value) || 0 };
                                  setStrategy({ ...strategy, signal_price_levels: levels });
                                }} />
                            </Field>

                            <Field label="Re-entry after SL">
                              <Select
                                value={(row.re_entry ? "yes" : "no")}
                                onValueChange={(v) => {
                                  const base = strategy.signal_price_levels || [
                                    { level: 66, target: 74, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 86, target: 99, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 116, target: 129, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                  ];
                                  const levels = [...base];
                                  levels[idx] = { ...levels[idx], re_entry: v === "yes" };
                                  setStrategy({ ...strategy, signal_price_levels: levels });
                                }}
                              >
                                <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="no">No</SelectItem>
                                  <SelectItem value="yes">Yes</SelectItem>
                                </SelectContent>
                              </Select>
                            </Field>
                            <Field label="Max re-entries">
                              <Input type="number" step="1" className="bg-zinc-900 border-zinc-800"
                                value={row.max_reentries ?? 0}
                                onChange={(e) => {
                                  const base = strategy.signal_price_levels || [
                                    { level: 66, target: 74, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 86, target: 99, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                    { level: 116, target: 129, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                                  ];
                                  const levels = [...base];
                                  levels[idx] = { ...levels[idx], max_reentries: parseInt(e.target.value) || 0 };
                                  setStrategy({ ...strategy, signal_price_levels: levels });
                                }} />
                            </Field>

                            <div className="text-[10px] text-zinc-500 font-mono pb-2 col-span-2 sm:col-span-1">
                              {(strategy.symbol || "?").toUpperCase()} · qty {row.quantity ?? 1} · SL−{row.sl_points ?? 2} · tgt {row.target}
                              {(row.trailing_sl > 0) ? ` · trail ${row.trailing_sl}` : ""}
                              {row.re_entry ? ` · re×${row.max_reentries ?? 0}` : ""}
                            </div>
                            </div>
                          </div>

                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>


          </section>

          {/* Derived */}
          <section className={SECTION}>
            <div className={HDR}>Derived Details (Auto-filled via Scrip Master)</div>
            {(() => {
              const locked = !!(strategy.locked_security_id);
              const sid = locked ? strategy.locked_security_id : (resolution?.security_id || "—");
              const strike = locked ? strategy.locked_strike : (resolution?.strike || "—");
              const expiry = locked ? (strategy.locked_expiry || "—") : (resolution?.expiry || "—");
              const tsym = locked ? (strategy.locked_trading_symbol || "—") : (resolution?.trading_symbol || "Run Resolve to auto-fill");
              return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 text-sm font-mono">
              <div className="bg-zinc-900 rounded px-3 py-3"><div className="text-[10px] text-zinc-500 uppercase">Security ID</div><div data-testid="derived-sec-id" className="text-amber-400">{sid}{locked ? " · LOCKED" : ""}</div></div>
              <div className="bg-zinc-900 rounded px-3 py-3"><div className="text-[10px] text-zinc-500 uppercase">Strike</div><div data-testid="derived-strike" className="text-amber-400">{strike}</div></div>
              <div className="bg-zinc-900 rounded px-3 py-3"><div className="text-[10px] text-zinc-500 uppercase">Spot LTP</div><div data-testid="derived-spot" className="text-emerald-400">{resolution?.spot_ltp ? `₹${resolution.spot_ltp}` : "—"}</div></div>
              <div className="bg-zinc-900 rounded px-3 py-3"><div className="text-[10px] text-zinc-500 uppercase">Expiry</div><div className="text-zinc-300">{expiry}</div></div>
              <div className="bg-zinc-900 rounded px-3 py-3 col-span-4"><div className="text-[10px] text-zinc-500 uppercase">Trading Symbol</div><div className="text-zinc-200 truncate">{tsym}</div></div>
            </div>
              );
            })()}
          </section>

          {/* Deploy CTA */}
          <div className="flex items-center gap-3">

          {/* Active Strategies — know which trade to stop/modify */}
          <div className={SECTION} data-testid="active-strategies-panel">
            
          <div className={`rounded-lg border border-sky-900/50 bg-sky-950/20 p-4 space-y-3 mb-4${HIDE_PARTNER_SECTIONS ? " hidden" : ""}`} aria-hidden={HIDE_PARTNER_SECTIONS}>
            <div className="text-[11px] uppercase tracking-[0.2em] text-sky-400/90 font-mono">Multi Wait Price</div>
            <div className="text-[10px] text-sky-200/90 font-mono leading-relaxed">
              Format: entry_price , lot , sl_pts , sl_trail_pts , target_pts , target_trail_pts , entry_band
            </div>
            <div className="text-[10px] text-zinc-500 font-mono">Sample: 100,1,5,2,15,3,3  (last = band pts · form default 0 · max 10)
            </div>
            <div className="text-[10px] text-zinc-500 font-mono">entry_band: BUY only if entry ≤ LTP ≤ entry+band (e.g. 100 & band 10 → 100–110). First entry + re-entry. NOT “how many times”. Re-entry still needs toggle ON.</div>
            {multiWaitMode && (
              <div className="text-[10px] text-amber-400">Box active — form Qty/SL/Trail/Target locked. Clear box to edit form.</div>
            )}
            <textarea
              data-testid="multi-wait-text"
              value={multiWaitText}
              onChange={(e) => setMultiWaitText(e.target.value)}
              placeholder={"Optional if form Entry filled\n100,1,5,2,15,3,3\n120,1,5,2,15,3,3"}
              className="w-full min-h-[88px] rounded border border-zinc-800 bg-zinc-950 px-3 py-2 font-mono text-xs text-sky-200 placeholder:text-zinc-600"
            />
            <Button
              data-testid="multi-wait-arm-btn"
              className="bg-sky-700 hover:bg-sky-600 text-white h-10"
              onClick={async () => {
                let lines = (multiWaitText || "").split(/\n|;/).map((x) => x.trim()).filter(Boolean);
                // Form Entry/SL/Target: empty Multi Wait box → single PRICE_WAIT from upper fields
                if (!lines.length) {
                  const formEp = Number(strategy.entry_price || 0);
                  if (formEp > 0) {
                    const qty = Number(strategy.quantity || 1) || 1;
                    const sl = Number(strategy.sl_points || 0);
                    const trail = Number(strategy.trailing_sl || 0);
                    const tgt = Number(strategy.target_gap || 0);
                    const tTrail = Number(strategy.target_trail_jump || 0);
                    const band = Math.min(10, Math.max(0, Number(strategy.entry_band_points ?? 0)));
                    lines = [formEp + "," + qty + "," + sl + "," + trail + "," + tgt + "," + tTrail + "," + band];
                    toast.success("Form Entry ₹" + formEp + " → Wait Price armed");
                  } else {
                    toast.error("Upar Entry price bharo, ya Multi Wait box mein line likho (e.g. 100,1,5,2,15,3,3)");
                    return;
                  }
                }
                // entry, lot, sl, sl_trail, target, target_trail, entry_band (pts around entry, not count)
                const items = [];
                for (const line of lines) {
                  const parts = line.split(/[,]+/).map((x) => x.trim()).filter((x) => x.length);
                  const ep = Number(parts[0]);
                  const qty = parts[1] != null && parts[1] !== "" ? Number(parts[1]) : Number(strategy.quantity) || 1;
                  if (!(ep > 0)) {
                    toast.error("Invalid entry: " + line);
                    return;
                  }
                  const item = { entry_price: ep, quantity: qty > 0 ? qty : 1 };
                  if (parts[2] != null && parts[2] !== "" && Number(parts[2]) >= 0) item.sl_points = Number(parts[2]);
                  if (parts[3] != null && parts[3] !== "" && Number(parts[3]) >= 0) item.trailing_sl = Number(parts[3]);
                  if (parts[4] != null && parts[4] !== "" && Number(parts[4]) >= 0) {
                    item.target_gap = Number(parts[4]);
                    item.t1_gap = Number(parts[4]);
                  }
                  if (parts[5] != null && parts[5] !== "" && Number(parts[5]) >= 0) {
                    item.target_trail_jump = Number(parts[5]);
                    item.target_trail_enabled = Number(parts[5]) > 0;
                  }
                  if (parts[6] != null && parts[6] !== "" && Number(parts[6]) >= 0) item.entry_band_points = Math.min(10, Math.max(0, Number(parts[6])));
                  else item.entry_band_points = strategy.entry_band_points ?? 0;
                  items.push(item);
                }
                try {
                  let lockedId = strategy.locked_security_id;
                  let lockedSym = strategy.locked_trading_symbol;
                  let lockedStrike = strategy.locked_strike;
                  let lockedLot = strategy.locked_lot_size || strategy.lot_size;
                  let lockedExp = strategy.locked_expiry;
                  if (!lockedId && resolution?.security_id) {
                    lockedId = String(resolution.security_id);
                    lockedSym = resolution.trading_symbol || "";
                    lockedStrike = Number(resolution.strike || 0);
                    lockedLot = Number(resolution.lot_size || strategy.lot_size || 0);
                    lockedExp = resolution.expiry || "";
                  }
                  if (!lockedId) {
                    const r = await axios.get(`${API}/scrip/resolve`, {
                      params: {
                        symbol: strategy.symbol,
                        instrument_family: strategy.instrument_family,
                        option_type: strategy.option_type,
                        moneyness: strategy.moneyness,
                        expiry_type: strategy.expiry_type,
                        manual_strike: strategy.manual_strike,
                        strike_selection: strategy.strike_selection,
                      },
                    });
                    if (!r.data?.security_id) {
                      toast.error("Resolve failed");
                      return;
                    }
                    setResolution(r.data);
                    lockedId = String(r.data.security_id);
                    lockedSym = r.data.trading_symbol || "";
                    lockedStrike = Number(r.data.strike || 0);
                    lockedLot = Number(r.data.lot_size || strategy.lot_size || 0);
                    lockedExp = r.data.expiry || "";
                  }
                  const base = {
                    ...strategy,
                    locked_security_id: lockedId,
                    locked_trading_symbol: lockedSym || "",
                    locked_strike: lockedStrike || 0,
                    locked_lot_size: lockedLot || 0,
                    locked_expiry: lockedExp || "",
                  };
                  delete base.id;
                  const res = await axios.post(`${API}/strategy/batch-price-wait`, { base, items });
                  await refreshStrategies();
                  if (res.data?.count) {
                    toast.success(`Armed ${res.data.count} Wait Price order(s)`);
                    setMultiWaitText("");
                  } else {
                    toast.error("Failed: " + JSON.stringify(res.data?.errors || res.data));
                  }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || e.message || "Multi Wait failed");
                }
              }}
            >
              Multi Wait Price
            </Button>
          </div>

<div className={HDR}><ListOrdered className="h-3.5 w-3.5" /> Today&apos;s Strategies (intraday)</div>
            {(() => {
              const isTodayIST = (iso) => {
                if (!iso) return false;
                try {
                  const d = new Date(iso);
                  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
                  const now = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
                  return ist.toISOString().slice(0, 10) === now.toISOString().slice(0, 10);
                } catch (_) { return false; }
              };
              const fmtIST = (iso) => {
                if (!iso) return "—";
                try {
                  return new Date(iso).toLocaleString("en-IN", {
                    timeZone: "Asia/Kolkata",
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
                  });
                } catch (_) { return String(iso).slice(0, 19); }
              };
              const visible = (myStrategies || [])
                .filter((s) => s.status !== "STOPPED")
                .filter((s) => isTodayIST(s.updated_at || s.created_at) || ["LIVE", "PRICE_WAIT", "SIGNAL_WAIT", "MOMENTUM_WAIT", "SCHEDULED"].includes(s.status) || s.pending_reentry)
                .sort((a, b) => {
                  const rank = (s) => {
                    if (s.pending_reentry) return 0;
                    const o = { LIVE: 1, SIGNAL_WAIT: 2, PRICE_WAIT: 3, MOMENTUM_WAIT: 4, SCHEDULED: 5, ERROR: 6, DRAFT: 7 };
                    return o[s.status] ?? 9;
                  };
                  return rank(a) - rank(b);
                });
              if (!visible.length) {
                return <div className="text-sm text-zinc-600 font-mono">No strategies for today (STOPPED hidden)</div>;
              }
              return (
              <div className="space-y-2">
                {visible.map((s) => {
                    const isPriceSignal = s.signal_enabled && s.signal_type === "PRICE_CROSS_UP";
                    const watchLabel = (s.signal_watch || "BOTH") === "BOTH" ? "CE+PE" : (s.signal_watch || "CE");
                    const sym = isPriceSignal
                      ? `${s.symbol} ${s.moneyness || "ITM2"} ${watchLabel} · Price Cross`.trim()
                      : (s.locked_trading_symbol || `${s.symbol} ${s.option_type || ""} ${s.locked_strike || ""}`.trim());
                    const badge =
                      s.pending_reentry ? "RE-ENTRY WAIT" :
                      s.status === "PRICE_WAIT" ? "PRICE WAIT" :
                      s.status === "MOMENTUM_WAIT" ? "MOMENTUM WAIT" :
                      s.status === "SIGNAL_WAIT" ? "AUTO MARKET WAIT" :
                      s.status === "SCHEDULED" ? "SCHEDULED" :
                      s.status === "ERROR" ? "ERROR" :
                      s.status === "DRAFT" ? "DRAFT" :
                      s.status === "LIVE" ? "LIVE" : (s.status || "—");
                    const tone =
                      s.pending_reentry ? "bg-amber-600/20 text-amber-400 border-amber-700" :
                      s.status === "PRICE_WAIT" ? "bg-sky-600/20 text-sky-400 border-sky-700" :
                      s.status === "MOMENTUM_WAIT" ? "bg-cyan-600/20 text-cyan-300 border-cyan-700" :
                      s.status === "SIGNAL_WAIT" ? "bg-violet-600/20 text-violet-300 border-violet-700" :
                      s.status === "ERROR" ? "bg-rose-600/20 text-rose-300 border-rose-700" :
                      s.status === "DRAFT" ? "bg-zinc-800/60 text-zinc-400 border-zinc-700" :
                      s.status === "LIVE" ? "bg-emerald-600/20 text-emerald-400 border-emerald-700" :
                      "bg-zinc-800 text-zinc-400 border-zinc-700";
                    const actionHint =
                      s.pending_reentry ? "Waiting for re-entry condition — STOP cancels re-entry" :
                      s.status === "PRICE_WAIT" ? "Waiting for entry price — STOP = cancel wait" :
                      s.status === "MOMENTUM_WAIT" ? "Uptick/Downtick multi — enters on reverse · STOP cancels" :
                      s.status === "SIGNAL_WAIT" ? "Auto MARKET wait — market entry when conditions pass" :
                      s.status === "LIVE" ? "Open algo trade — STOP = cancel this strategy orders" :
                      s.status === "ERROR" ? "Failed — Remove to clear, or Open/Modify to fix" :
                      s.status === "DRAFT" ? "Draft — Remove or Deploy" :
                      s.status === "SCHEDULED" ? "Scheduled — will arm at entry time" :
                      "STOP cancels this strategy";
                    const canRemove = s.status === "ERROR" || s.status === "DRAFT";
                    const when = fmtIST(s.updated_at || s.created_at);
                    return (
                      <div key={s.id} className="rounded-lg border border-zinc-800 bg-zinc-950/80 p-3">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${tone}`}>{badge}</span>
                              <span className="text-sm font-semibold text-zinc-100 truncate">{s.name || "Strategy"}</span>
                              <span className="text-[10px] font-mono text-zinc-600">{String(s.id).slice(0,8)}</span>
                            </div>
                            <div className="text-[11px] font-mono text-sky-300/90 mb-1">{when} IST</div>
                            <div className="text-xs text-zinc-300 font-mono truncate">{sym}</div>
                            <div className="text-[11px] text-zinc-500 mt-1">
                              <div>{s.side} · Entry ₹{s.entry_price || "—"} · Qty {s.quantity} · {s.product_type}</div>
                              {isPriceSignal ? (
                                <>
                                  <div className="text-amber-400/90">Auto Signal ON · Watch {watchLabel} · Max {s.signal_max_fires ?? 5} trades · Used {s.signal_fire_count || 0}</div>
                                  <div>Levels: {(s.signal_price_levels || []).map((lv) => `${lv.level}→${lv.target}`).join(" · ") || "66→74 · 86→99 · 116→129"}</div>
                                </>
                              ) : (
                                <>
                              <div>SL {s.sl_points}pts · Tgt {s.target_gap} · Trail {s.trailing_sl || 0}pts ({s.trailing_mode || "COMMON"})</div>
                              <div>Re-entry SL:{String(!!s.re_entry_on_sl)} TGT:{String(!!s.re_entry_on_target)} · Max {s.max_reentries} · Used {s.reentry_count || 0}</div>
                                </>
                              )}
                              {s.deployed_order_ids?.length ? ` · Orders: ${s.deployed_order_ids.join(", ")}` : ""}
                            </div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">{actionHint}</div>
                          </div>
                          <div className="flex flex-row sm:flex-col gap-1.5 shrink-0 w-full sm:w-auto mt-2 sm:mt-0">
                            <Button size="sm" variant="outline" className="h-9 flex-1 sm:flex-none border-zinc-700 text-zinc-300 text-xs sm:text-sm px-2"
                              onClick={() => loadStrategyIntoForm(s)}>
                              Open
                            </Button>
                            {canRemove ? (
                              <Button size="sm" variant="outline" className="h-9 flex-1 sm:flex-none border-zinc-600 text-zinc-400 hover:bg-zinc-800 text-xs sm:text-sm px-2"
                                onClick={async () => {
                                  try {
                                    await axios.delete(`${API}/strategy/${s.id}`);
                                    await refreshStrategies();
                                    toast.success("Removed " + (s.name || s.id.slice(0, 8)));
                                  } catch (e) {
                                    toast.error(e?.response?.data?.detail || e.message || "Remove failed");
                                  }
                                }}>
                                Remove
                              </Button>
                            ) : (
                              <Button size="sm" variant="destructive" className="h-9 flex-1 sm:flex-none bg-rose-700 hover:bg-rose-600 text-xs sm:text-sm px-2"
                                disabled={loadingExitId === s.id}
                                data-testid={`exit-btn-${String(s.id).slice(0,8)}`}
                                onClick={() => exitStrategy(s.id, `${s.name} · ${sym}`)}>
                                <OctagonX className="h-3.5 w-3.5 mr-1" />
                                {loadingExitId === s.id ? "…" : "STOP"}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              );
            })()}
          </div>

            <div className="rk-sticky-actions flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full lg:col-span-12">
            <Button onClick={openDeployConfirm} disabled={loading.deploy} data-testid="deploy-btn" className={`flex-1 h-14 text-base font-black tracking-wider ${broker.kill_switch ? "bg-zinc-700 hover:bg-zinc-600" : broker.mode === "LIVE" ? "bg-rose-600 hover:bg-rose-500" : "bg-amber-500 hover:bg-amber-400 text-black"}`}>
              <Rocket className="h-5 w-5 mr-2" /> {loading.deploy ? "Deploying…" : `DEPLOY TO ${broker.mode}`}
            </Button>
            {(strategy.status === "LIVE" || strategy.status === "PRICE_WAIT" || strategy.status === "MOMENTUM_WAIT" || strategy.status === "SIGNAL_WAIT" || strategy.pending_reentry) && strategy.id ? (
              <Button
                onClick={() => exitStrategy(strategy.id, `${strategy.name} · ${strategy.locked_trading_symbol || strategy.symbol}`)}
                disabled={loadingExitId === strategy.id}
                data-testid="exit-current-btn"
                className="h-14 px-6 sm:w-auto w-full bg-zinc-800 hover:bg-rose-800 border border-rose-700/50 text-rose-300 font-bold"
              >
                <OctagonX className="h-5 w-5 mr-2" />
                {loadingExitId === strategy.id ? "Stopping…" : "STOP"}
              </Button>
            ) : null}
                        
            <Button style={{ display: HIDE_PARTNER_SECTIONS ? "none" : undefined }}
              onClick={async () => {
                try {
                  const anyCond = strategy.cond_vwap || strategy.cond_ema || strategy.cond_rsi || strategy.cond_bb || strategy.cond_sqrt_ema || strategy.cond_ema50 || strategy.cond_ema200 || strategy.cond_fvg_bullish || strategy.cond_no_lower_wick_30m;
                  if (!anyCond) {
                    toast.error("Turn ON at least one: VWAP / EMA / RSI / BB / Special volume / Sqrt EMA");
                    return;
                  }
                  // Always NEW strategy id — do NOT overwrite an existing SIGNAL_WAIT/LIVE card
                  // (user may arm 1-lot wait then 5-lot wait; both must remain visible)
                  const newId = (typeof crypto !== "undefined" && crypto.randomUUID)
                    ? crypto.randomUUID()
                    : `sig-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
                  const saved = await axios.post(`${API}/strategy`, {
                    ...strategy,
                    id: newId,
                    status: "DRAFT",
                    entry_type: "MARKET",
                    entry_price: 0,
                    signal_wait_enabled: false,
                    pending_reentry: false,
                    reentry_count: 0,
                    created_at: new Date().toISOString(),
                  });
                  const sid = saved.data.id;
                  setStrategy(saved.data);
                  const armed = await axios.post(`${API}/strategy/${sid}/arm-signal`);
                  toast.success(`Auto MARKET armed — market entry when conditions pass (${armed.data.locked_trading_symbol || ""})`);
                  // refresh list
                  try {
                    const list = await axios.get(`${API}/strategies`);
                    // ignore shape
                  } catch (e) {}
                } catch (e) {
                  toast.error(e?.response?.data?.detail || e.message || "Auto MARKET failed");
                }
              }}
              variant="outline"
              data-testid="arm-signal-btn"
              className="border-violet-700 text-violet-300 hover:bg-violet-950 h-14"
              title="No entry price needed. Auto MARKET when Buy Entry Conditions pass"
            >
              Auto MARKET
            </Button>
<Button
              onClick={async () => {
                try {
                  const et = (strategy.entry_time || "09:15:00").trim();
                  const xt = (strategy.exit_time || "23:15").trim();
                  // Always new id when form already points at an active/scheduled card
                  // so CE + PE (or two legs) both stay scheduled independently
                  const activeStatuses = ["LIVE", "PRICE_WAIT", "SIGNAL_WAIT", "MOMENTUM_WAIT", "SCHEDULED", "PENDING_REENTRY"];
                  const needNew = activeStatuses.includes(String(strategy.status || "").toUpperCase());
                  const newId = needNew
                    ? ((typeof crypto !== "undefined" && crypto.randomUUID)
                        ? crypto.randomUUID()
                        : `sch-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`)
                    : strategy.id;
                  let payload = { ...strategy, id: newId, entry_time: et, exit_time: xt, status: "SCHEDULED" };
                  // Price Cross: signal_watch is source of truth for CE/PE
                  if (payload.signal_enabled && payload.signal_type === "PRICE_CROSS_UP") {
                    const w = String(payload.signal_watch || "BOTH").toUpperCase();
                    if (w === "CE" || w === "PE") {
                      payload = { ...payload, option_type: w, signal_watch: w };
                    } else {
                      payload = { ...payload, signal_watch: "BOTH" };
                    }
                  }
                  const saved = await axios.post(`${API}/strategy`, payload);
                  const nextId = (typeof crypto !== "undefined" && crypto.randomUUID)
                    ? crypto.randomUUID()
                    : `draft-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
                  // After CE schedule, flip form to PE for easy second leg (and vice versa)
                  const just = String(payload.signal_watch || payload.option_type || "CE").toUpperCase();
                  const flip = just === "CE" ? "PE" : (just === "PE" ? "CE" : "PE");
                  setStrategy({
                    ...payload,
                    ...(saved.data || {}),
                    id: nextId,
                    status: "DRAFT",
                    option_type: flip,
                    signal_watch: flip,
                  });
                  await refreshStrategies();
                  toast.success(
                    "Scheduled " + (payload.signal_watch || payload.option_type || "") +
                    " · " + (payload.symbol || "") + " @ " + et +
                    " — form ready for " + flip
                  );
                } catch (e) {
                  toast.error(e?.response?.data?.detail || e.message || "Schedule failed");
                }
              }}
              variant="outline"
              data-testid="schedule-btn"
              className="border-emerald-700 text-emerald-400 hover:bg-emerald-950 h-12 sm:h-14 text-sm sm:text-base flex-1 min-w-0"
            >
              ⏰ Schedule
            </Button>
            <Button
              onClick={async () => {
                try {
                  // Always save as a NEW strategy id so previous DRAFT/LIVE are never overwritten
                  const newId = (typeof crypto !== "undefined" && crypto.randomUUID)
                    ? crypto.randomUUID()
                    : `draft-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
                  let payload = {
                    ...strategy,
                    id: newId,
                    entry_time: (strategy.entry_time || "09:15:00").trim(),
                    exit_time: (strategy.exit_time || "23:15").trim(),
                    status: strategy.status === "SCHEDULED" ? "SCHEDULED" : "DRAFT",
                    created_at: new Date().toISOString(),
                    reentry_count: 0,
                    pending_reentry: false,
                    signal_fire_count: 0,
                  };
                  if (payload.signal_enabled && payload.signal_type === "PRICE_CROSS_UP") {
                    const useMan = String(payload.strike_selection || "").toUpperCase() === "MANUAL" && Number(payload.manual_strike) > 0;
                    payload = {
                      ...payload,
                      strike_selection: useMan ? "MANUAL" : "AUTO",
                      manual_strike: useMan ? Number(payload.manual_strike) : 0,
                      locked_security_id: "",
                      locked_strike: 0,
                      locked_trading_symbol: "",
                      locked_expiry: "",
                      signal_price_levels: (payload.signal_price_levels && payload.signal_price_levels.length)
                        ? payload.signal_price_levels
                        : [
                            { level: 66, target: 74, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                            { level: 86, target: 99, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                            { level: 116, target: 129, sl_points: 2, quantity: 1, trailing_sl: 0, re_entry: false, max_reentries: 0 },
                          ],
                    };
                  }
                  const saved = await axios.post(`${API}/strategy`, payload);
                  setStrategy({ ...payload, ...(saved.data || {}) });
                  await refreshStrategies();
                  toast.success(
                    "Saved new " + (payload.status || "DRAFT") + " · " + (payload.name || payload.symbol || "") +
                    " (previous strategies kept)"
                  );
                } catch (e) {
                  toast.error(e?.response?.data?.detail || e.message || "Save failed");
                }
              }}
              variant="outline"
              className="border-zinc-700 hover:bg-zinc-900 h-12 sm:h-14 text-sm sm:text-base flex-1 min-w-0"
              data-testid="save-btn"
            >
              <Save className="h-4 w-4 mr-2" /> Save
            </Button>
          </div>
          </div>


          {/* Stock Scanner */}
          <section className={SECTION} data-testid="section-stock-scanner">
            <div className={HDR}>Stock Scanner</div>
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end mb-3">
              <div className="flex-1 w-full">
                <div className="text-[10px] text-zinc-500 mb-1 uppercase tracking-wide">Scanner preset</div>
                <select
                  data-testid="scanner-dropdown"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
                  value={scannerId}
                  onChange={(e) => setScannerId(e.target.value)}
                >
                  {(scannerPresets.length ? scannerPresets : [{ id: "stock_screanner_001", name: "stock screanner 001" }]).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <Button
                data-testid="scanner-run-btn"
                onClick={() => runStockScanner(false)}
                disabled={scannerLoading}
                className="bg-violet-600 hover:bg-violet-500"
              >
                {scannerLoading ? "Scanning…" : "Run Scan"}
              </Button>
              <Button
                variant="outline"
                className="border-zinc-700"
                disabled={scannerLoading}
                onClick={() => runStockScanner(true)}
              >
                Force Refresh
              </Button>
            </div>
            
            
            {scannerResult && (
              <div className="space-y-2">
                <div className="text-xs text-zinc-400 font-mono mb-1">
                  Matches: <span className="text-emerald-400 font-bold">{scannerResult.match_count ?? 0}</span>
                </div>
                {(scannerResult.matches || []).length === 0 ? (
                  <div className="text-zinc-600 text-sm py-4 text-center">No stocks passed all filters.</div>
                ) : (
                  <div className="overflow-auto max-h-64 border border-zinc-800 rounded">
                    <table className="w-full text-xs font-mono">
                      <thead className="bg-zinc-900 text-zinc-500 sticky top-0">
                        <tr>
                          <th className="text-left p-2 whitespace-nowrap">Stock Name</th>
                          <th className="text-left p-2">Symbol</th>
                          <th className="text-left p-2">Cross</th>
                          <th className="text-right p-2 whitespace-nowrap">Close %</th>
                          <th className="text-right p-2 whitespace-nowrap">Last Close</th>
                          <th className="text-right p-2">LTP</th>
                          <th className="text-right p-2">RSI</th>
                          <th className="text-right p-2">ADX</th>
                          <th className="text-right p-2">Volume</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scannerResult.matches.map((m) => (
                          <tr key={m.symbol} className="border-t border-zinc-900 hover:bg-zinc-900/50">
                            <td className="p-2 text-zinc-300 max-w-[140px] truncate" title={m.name || m.trading_symbol || ""}>{m.name || m.trading_symbol || "—"}</td>
                            <td className="p-2 text-sky-400 font-semibold">{m.symbol}</td>
                            <td className="p-2 text-violet-300 text-[10px]">{(m.crosses && m.crosses.length) ? m.crosses.join(",") : "-"}</td>
                            <td className={`p-2 text-right font-semibold ${(m.chg_pct ?? m.pct_from_2d) > 0 ? "text-emerald-400" : (m.chg_pct ?? m.pct_from_2d) < 0 ? "text-rose-400" : "text-zinc-400"}`}>
                              {(m.chg_pct ?? m.pct_from_2d) != null ? `${Number(m.chg_pct ?? m.pct_from_2d).toFixed(2)}%` : "—"}
                            </td>
                            <td className="p-2 text-right">{m.last_close ?? m.close_1d_ago ?? "—"}</td>
                            <td className="p-2 text-right text-amber-300 font-semibold">{m.ltp ?? "—"}</td>
                            <td className="p-2 text-right">{m.rsi ?? "—"}</td>
                            <td className="p-2 text-right">{m.adx ?? "—"}</td>
                            <td className="p-2 text-right">{m.volume?.toLocaleString?.() ?? m.volume ?? "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Tabs: Orders / Positions / Logs */}
          <Tabs defaultValue="trades" className="mt-4">
            <TabsList className="bg-zinc-900 border border-zinc-800">
              <TabsTrigger value="trades" data-testid="tab-trades">Trades</TabsTrigger>
              <TabsTrigger value="positions" data-testid="tab-positions">Positions</TabsTrigger>
              <TabsTrigger value="orders" data-testid="tab-orders">Super Orders</TabsTrigger>
              <TabsTrigger value="logs" data-testid="tab-logs">Audit Logs</TabsTrigger>
            </TabsList>
            <TabsContent value="logs">
              <Card className="bg-zinc-950 border-zinc-800 p-4">
                <div className="flex justify-between mb-3">
                  <div className="text-xs font-mono uppercase text-zinc-500">Recent events</div>
                  <Button size="sm" variant="ghost" onClick={clearLogs} data-testid="clear-logs-btn"><Trash2 className="h-3 w-3 mr-1" /> Clear</Button>
                </div>
                <div className="space-y-1 max-h-96 overflow-auto text-xs font-mono">
                  {logs.length === 0 && <div className="text-zinc-600 py-8 text-center">No events yet. Apply credentials to start.</div>}
                  {logs.map((l) => (
                    <div key={l.id} className="px-2 py-1.5 border-b border-zinc-900">
                      <div className="flex gap-3 items-start">
                        <span className="text-zinc-600 w-36 shrink-0">{new Date(l.ts).toLocaleString()}</span>
                        <Badge variant="outline" className={"shrink-0 " + (l.kind === "ERROR" ? "border-rose-800 text-rose-400" : l.kind === "ORDER" ? "border-amber-800 text-amber-400" : "border-zinc-700 text-zinc-400")}>{l.kind}</Badge>
                        <div className="min-w-0 flex-1">
                          <div className="text-zinc-300 break-words">{l.message}</div>
                          {l.strategy_id ? <div className="text-[10px] text-zinc-600">strategy {String(l.strategy_id).slice(0, 8)}</div> : null}
                          {l.payload && Object.keys(l.payload).length > 0 ? (
                            <details className="mt-1">
                              <summary className="text-[10px] text-zinc-500 cursor-pointer">details / reject info</summary>
                              <pre className="text-[10px] text-zinc-500 whitespace-pre-wrap break-all mt-1 max-h-40 overflow-auto">{JSON.stringify(l.payload, null, 2)}</pre>
                            </details>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>
            <TabsContent value="orders">
              <Card className="bg-zinc-950 border-zinc-800 p-4 text-xs font-mono">
                <div className="text-[10px] text-zinc-500 mb-2 uppercase">Super Orders · Limit / Fill / SL / Target / Trail</div>
                {orders.length === 0 ? <div className="text-zinc-600 py-8 text-center">No super orders. Deploy to create.</div> : (
                  <div className="space-y-3 max-h-[28rem] overflow-auto">
                    {orders.map((o) => {
                      const legs = o.legDetails || o.leg_details || [];
                      const sl = legs.find((l) => (l.legName || l.leg_name) === "STOP_LOSS_LEG") || {};
                      const tg = legs.find((l) => (l.legName || l.leg_name) === "TARGET_LEG") || {};
                      const st = (x) => (x.orderStatus || x.status || "—");
                      const hit = (x) => {
                        const s = String(st(x)).toUpperCase();
                        const tq = Number(x.triggeredQuantity || x.triggered_quantity || 0);
                        return s.includes("TRIG") || s.includes("TRADE") || s.includes("COMPLETE") || tq > 0;
                      };
                      const rawMsg = String(o.omsErrorDescription || o.oms_error_description || o.statusMessage || o.status_message || o.rejectionReason || o.rejection_reason || "").trim();
                      const msgUp = rawMsg.toUpperCase();
                      const successMsg = /^(TRADE\s*CONFIRMED|CONFIRMED|SUCCESS|OK|TRADED|TRANSIT|PENDING|ACTIVE|COMPLETE|COMPLETED|TRIGGERED|CANCELLED|CANCELED)?$/.test(msgUp)
                        || /TRADE\s*CONFIRMED/i.test(rawMsg)
                        || /ORDER\s*PLACED/i.test(rawMsg)
                        || /SUCCESS/i.test(rawMsg);
                      const looksError = !!(o.omsErrorDescription || o.oms_error_description || o.rejectionReason || o.rejection_reason)
                        || /REJECT|ERROR|FAIL|INVALID|DH-\d+|NOT\s*ALLOWED|INSUFFICIENT/i.test(rawMsg);
                      const rej = (rawMsg && looksError && !successMsg) ? rawMsg : "";
                      const tCreate = o.createTime || o.create_time || o.exchangeTime || "";
                      const tUpdate = o.updateTime || o.update_time || "";
                      const trail = sl.trailingJump ?? sl.trailing_jump ?? o.trailingJump ?? 0;
                      const avg = Number(o.averageTradedPrice || o.average_traded_price || o.avgTradedPrice || o.tradedPrice || o.traded_price || 0) || 0;
                      const limitPx = o.price ?? o.orderPrice ?? o.order_price ?? null;
                      const filled = o.filledQty || o.filled_qty || 0;
                      const corr = o.correlationId || o.correlation_id || "";
                      return (
                        <div key={o.orderId || o.order_id || Math.random()} className="border border-zinc-800 rounded-lg p-3 bg-zinc-900/40">
                          <div className="flex flex-wrap gap-x-4 gap-y-1 justify-between">
                            <div>
                              <span className="text-amber-400 font-semibold">{o.tradingSymbol || o.trading_symbol || o.securityId || "—"}</span>
                              <span className={(o.transactionType || o.transaction_type) === "BUY" ? " text-emerald-400 ml-2" : " text-rose-400 ml-2"}>
                                {o.transactionType || o.transaction_type || "—"}
                              </span>
                              <span className="text-zinc-500 ml-2">qty {o.quantity}</span>
                              {corr ? <span className="text-zinc-600 ml-2">{corr}</span> : null}
                            </div>
                            <Badge className="bg-zinc-800">{o.orderStatus || o.order_status || "—"}</Badge>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2 text-[11px]">
                            <div><div className="text-zinc-500">Limit</div><div className="text-zinc-200">₹{limitPx ?? "—"}</div></div>
                            <div><div className="text-zinc-500">Fill / Avg</div><div className={avg ? "text-emerald-400 font-semibold" : "text-zinc-200"}>{avg ? ("₹" + avg) : "—"}</div></div>
                            <div><div className="text-zinc-500">Filled</div><div className="text-zinc-200">{filled}/{o.quantity}</div></div>
                            <div><div className="text-zinc-500">Created</div><div className="text-zinc-300">{tCreate || "—"}</div></div>
                            <div><div className="text-zinc-500">Updated</div><div className="text-zinc-300">{tUpdate || "—"}</div></div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 text-[11px]">
                            <div className={"rounded border px-2 py-1.5 " + (hit(sl) ? "border-rose-800/60 bg-rose-950/30" : "border-zinc-800")}>
                              <div className="text-zinc-500">Stop Loss {hit(sl) ? "· HIT" : ""}</div>
                              <div>₹{sl.price ?? "—"} · <span className="text-zinc-400">{st(sl)}</span></div>
                              {trail ? <div className="text-zinc-500">Trail jump {trail}</div> : null}
                            </div>
                            <div className={"rounded border px-2 py-1.5 " + (hit(tg) ? "border-emerald-800/60 bg-emerald-950/30" : "border-zinc-800")}>
                              <div className="text-zinc-500">Target {hit(tg) ? "· HIT" : ""}</div>
                              <div>₹{tg.price ?? "—"} · <span className="text-zinc-400">{st(tg)}</span></div>
                              {Number(tg.triggeredQuantity || tg.triggered_quantity || 0) > 0 ? (
                                <div className="text-zinc-500">Trig qty {tg.triggeredQuantity || tg.triggered_quantity}</div>
                              ) : null}
                            </div>
                            <div className="rounded border border-zinc-800 px-2 py-1.5">
                              <div className="text-zinc-500">Order ID</div>
                              <div className="text-amber-400/90 break-all">{o.orderId || o.order_id || "—"}</div>
                            </div>
                          </div>
                          {rej ? (
                            <div className="mt-2 text-rose-400 text-[11px] border border-rose-900/40 rounded px-2 py-1">
                              Reject / Error: {rej}
                            </div>
                          ) : null}
                          {legs.length > 2 ? (
                            <div className="mt-2 text-[10px] text-zinc-500">
                              Legs: {legs.map((l) => (l.legName || l.leg_name) + "=" + st(l)).join(" · ")}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </TabsContent>
                        <TabsContent value="positions">
              <Card className="bg-zinc-950 border-zinc-800 p-3 sm:p-4 text-xs font-mono overflow-hidden">
                {(() => {
                  const rows = positions || [];
                  const netPnl = rows.reduce((a, p) => a + Number(p.unrealizedProfit ?? 0) + Number(p.realizedProfit ?? 0), 0);
                  const openN = rows.filter((p) => Number(p.netQty || p.positionQty || 0) !== 0).length;
                  return (
                    <>
                      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
                        <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Open Positions · Option detail</div>
                        <div className="flex flex-wrap gap-3 text-[11px]">
                          <span className="text-zinc-500">Open <b className="text-zinc-200">{openN}</b></span>
                          <span className={netPnl >= 0 ? "text-emerald-400" : "text-rose-400"}>
                            Net P&L <b>{netPnl >= 0 ? "+" : ""}₹{netPnl.toFixed(2)}</b>
                          </span>
                        </div>
                      </div>
                      {rows.length === 0 ? (
                        <div className="text-zinc-600 py-8 text-center">No open positions.</div>
                      ) : (
                        <div className="overflow-x-auto max-h-[28rem]">
                          <table className="w-full min-w-[720px] border-collapse">
                            <thead>
                              <tr className="text-zinc-500 uppercase text-[9px] tracking-wider border-b border-zinc-800">
                                <th className="text-left py-2 pr-2">Exchange</th>
                                <th className="text-left py-2 pr-2">Symbol</th>
                                <th className="text-center py-2 px-1">B/S</th>
                                <th className="text-center py-2 px-1">Qty</th>
                                <th className="text-right py-2 px-1">Avg Entry</th>
                                <th className="text-right py-2 px-1">LTP</th>
                                <th className="text-right py-2 px-1">Unreal P&L</th>
                                <th className="text-right py-2 px-1">Realized</th>
                                <th className="text-right py-2 pl-1">Net P&L</th>
                                <th className="text-center py-2 pl-2">Product</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((p, i) => {
                                const qty = Number(p.netQty ?? p.positionQty ?? 0);
                                const side = qty > 0 ? "BUY" : qty < 0 ? "SELL" : (p.positionType || "—");
                                const avg = Number(p.buyAvg ?? p.costPrice ?? p.averagePrice ?? 0);
                                const ltp = Number(p.ltp ?? 0);
                                const ur = Number(p.unrealizedProfit ?? 0);
                                const rz = Number(p.realizedProfit ?? 0);
                                const net = ur + rz;
                                const exch = String(p.exchangeSegment || p.exchange || "—")
                                  .replace("NSE_FNO", "NFO").replace("BSE_FNO", "BFO").replace("MCX_COMM", "MCX");
                                const sym = p.tradingSymbol || p.trading_symbol || p.securityId || "—";
                                const prod = p.productType || p.product || "—";
                                const sideCls = side === "BUY" ? "bg-emerald-900/50 text-emerald-400" : side === "SELL" ? "bg-rose-900/50 text-rose-400" : "text-zinc-500";
                                const pnlCls = net >= 0 ? "text-emerald-400" : "text-rose-400";
                                return (
                                  <tr key={p.securityId || sym || i} className="border-b border-zinc-900/80 hover:bg-zinc-900/40">
                                    <td className="py-2.5 pr-2"><span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 text-[9px]">{exch}</span></td>
                                    <td className="py-2.5 pr-2 text-zinc-200 font-medium whitespace-nowrap">{sym}</td>
                                    <td className="text-center py-2.5"><span className={"px-2 py-0.5 rounded text-[10px] font-semibold " + sideCls}>{side}</span></td>
                                    <td className="text-center py-2.5 text-zinc-300">{Math.abs(qty)}</td>
                                    <td className="text-right py-2.5 text-zinc-300">₹{avg ? avg.toFixed(2) : "—"}</td>
                                    <td className="text-right py-2.5 text-amber-400/90">₹{ltp ? ltp.toFixed(2) : "—"}</td>
                                    <td className={"text-right py-2.5 " + (ur >= 0 ? "text-emerald-400" : "text-rose-400")}>{ur >= 0 ? "+" : ""}₹{ur.toFixed(2)}</td>
                                    <td className={"text-right py-2.5 " + (rz >= 0 ? "text-emerald-400" : "text-rose-400")}>{rz >= 0 ? "+" : ""}₹{rz.toFixed(2)}</td>
                                    <td className={"text-right py-2.5 font-semibold " + pnlCls}>{net >= 0 ? "+" : ""}₹{net.toFixed(2)}</td>
                                    <td className="text-center py-2.5 pl-2 text-zinc-500 text-[10px]">{prod}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  );
                })()}
              </Card>
            </TabsContent>

            <TabsContent value="trades">
              <Card className="bg-zinc-950 border-zinc-800 p-3 sm:p-4 text-xs font-mono overflow-hidden">
                {(() => {
                  const rows = trades || [];
                  const net = Number(tradesNetPnl || 0);
                  const fmtT = (x) => {
                    if (!x) return "—";
                    try {
                      const d = new Date(x);
                      if (!isNaN(d.getTime())) {
                        return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false });
                      }
                    } catch (e) {}
                    return String(x).replace("T", " ").slice(0, 19);
                  };
                  const stBadge = (s) => {
                    const u = String(s || "");
                    if (/Target/i.test(u)) return "bg-emerald-900/40 text-emerald-400";
                    if (/SL Hit/i.test(u)) return "bg-rose-900/40 text-rose-400";
                    if (/Reject/i.test(u)) return "bg-rose-950 text-rose-500";
                    if (/Cancel/i.test(u)) return "bg-zinc-800 text-zinc-500";
                    if (/Open|Pend|Active|Fill/i.test(u)) return "bg-amber-900/30 text-amber-400";
                    return "bg-zinc-800 text-zinc-400";
                  };
                  const legBadge = (s) => {
                    const u = String(s || "").toUpperCase();
                    if (u === "HIT") return "text-emerald-400";
                    if (u === "CANCELLED" || u === "CANCELLED") return "text-zinc-500";
                    if (u === "REJECTED") return "text-rose-500";
                    if (u === "PENDING") return "text-amber-400/80";
                    return "text-zinc-500";
                  };
                  return (
                    <>
                      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
                        <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Trades · Entry / Exit / SL / Target</div>
                        <div className="flex flex-wrap gap-3 text-[11px]">
                          <span className="text-zinc-500">{rows.length} trades</span>
                          <span className={net >= 0 ? "text-emerald-400" : "text-rose-400"}>
                            Net P&L <b>{net >= 0 ? "+" : ""}₹{net.toFixed(2)}</b>
                          </span>
                        </div>
                      </div>
                      {rows.length === 0 ? (
                        <div className="text-zinc-600 py-8 text-center">No trades yet. Deploy a strategy — entry/exit times appear here live.</div>
                      ) : (
                        <div className="overflow-x-auto max-h-[34rem]">
                          <table className="w-full min-w-[1100px] border-collapse">
                            <thead>
                              <tr className="text-zinc-500 uppercase text-[9px] tracking-wider border-b border-zinc-800">
                                <th className="text-left py-2 pr-2">Entry Time</th>
                                <th className="text-left py-2 pr-2">Exit Time</th>
                                <th className="text-left py-2 pr-2">Exchange</th>
                                <th className="text-left py-2 pr-2">Symbol</th>
                                <th className="text-center py-2 px-1">B/S</th>
                                <th className="text-center py-2 px-1">Qty</th>
                                <th className="text-right py-2 px-1">Entry ₹</th>
                                <th className="text-right py-2 px-1">Exit ₹</th>
                                <th className="text-right py-2 px-1">SL ₹</th>
                                <th className="text-center py-2 px-1">SL Status</th>
                                <th className="text-right py-2 px-1">Target ₹</th>
                                <th className="text-center py-2 px-1">Tgt Status</th>
                                <th className="text-center py-2 px-1">Status</th>
                                <th className="text-right py-2 pl-1">P&L</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((r, i) => {
                                const side = String(r.side || "BUY").toUpperCase();
                                const sideCls = side === "BUY" ? "bg-emerald-900/50 text-emerald-400" : "bg-rose-900/50 text-rose-400";
                                const pnl = r.pnl;
                                const pnlCls = pnl == null ? "text-zinc-600" : pnl >= 0 ? "text-emerald-400" : "text-rose-400";
                                return (
                                  <tr key={r.order_id || i} className="border-b border-zinc-900/80 hover:bg-zinc-900/40 align-top">
                                    <td className="py-2.5 pr-2 text-zinc-400 whitespace-nowrap text-[10px]">{fmtT(r.entry_time)}</td>
                                    <td className="py-2.5 pr-2 text-zinc-400 whitespace-nowrap text-[10px]">{fmtT(r.exit_time)}</td>
                                    <td className="py-2.5 pr-2"><span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 text-[9px]">{r.exchange || "—"}</span></td>
                                    <td className="py-2.5 pr-2 text-zinc-200 whitespace-nowrap font-medium">{r.symbol || "—"}</td>
                                    <td className="text-center py-2.5"><span className={"px-2 py-0.5 rounded text-[10px] font-semibold " + sideCls}>{side}</span></td>
                                    <td className="text-center py-2.5 text-zinc-300">{r.quantity || "—"}</td>
                                    <td className="text-right py-2.5 text-zinc-300">₹{r.entry_price ? Number(r.entry_price).toFixed(2) : "—"}</td>
                                    <td className="text-right py-2.5 text-zinc-300">₹{r.exit_price ? Number(r.exit_price).toFixed(2) : "—"}</td>
                                    <td className="text-right py-2.5 text-rose-400/80">₹{r.sl_price ? Number(r.sl_price).toFixed(2) : "—"}</td>
                                    <td className={"text-center py-2.5 text-[10px] font-semibold " + legBadge(r.sl_status)}>{r.sl_status || "—"}</td>
                                    <td className="text-right py-2.5 text-emerald-400/80">₹{r.target_price ? Number(r.target_price).toFixed(2) : "—"}</td>
                                    <td className={"text-center py-2.5 text-[10px] font-semibold " + legBadge(r.target_status)}>{r.target_status || "—"}</td>
                                    <td className="text-center py-2.5"><span className={"px-2 py-0.5 rounded text-[9px] " + stBadge(r.status)}>{r.status || "—"}</span></td>
                                    <td className={"text-right py-2.5 font-semibold " + pnlCls}>
                                      {pnl == null ? "—" : `${pnl >= 0 ? "+" : ""}₹${Number(pnl).toFixed(2)}`}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  );
                })()}
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>

      <footer className="px-4 sm:px-6 py-4 border-t border-zinc-900 text-[9px] sm:text-[10px] text-zinc-600 font-mono uppercase tracking-widest flex flex-col sm:flex-row gap-2 justify-between">
        <div className="break-words">DhanHQ v2 · Static IP: {publicCfg.static_ip ? publicCfg.static_ip.slice(0, 16) + "…" : "Not set"} · Rate {publicCfg.max_orders_per_second}/s</div>
        <div className="flex items-center gap-2"><Activity className="h-3 w-3 text-emerald-500" /> Engine online</div>
      </footer>
    </div>
  );
}
